/**
 * Travel Tools - Map-based language notes and travel tracking (formerly My Morocco)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Import refactored components
import { PriceDisplay, BudgetSetupCard, QuickCalculator, WishlistCard } from './components/morocco';
import { useTranslation } from './i18n/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create custom marker icon — circular "balloon" style with white border, shadow, and a small
// tail pointer below pointing to the exact location. Used for BOTH image-backed markers (when
// admin/user uploaded a photo) and emoji-fallback markers (when no photo). Pure CSS overhaul —
// no new features, just makes markers look like a proper pin instead of floating text.
const createEmojiIcon = (emoji, imageUrl) => {
  // Shared "balloon + tail" wrapper — same dimensions for image and emoji variants so all
  // markers line up consistently on the map. 40px circle + 8px tail = 48px total height.
  const tail = `<div style="position:absolute;left:50%;bottom:0;transform:translateX(-50%);width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:8px solid #ffffff;filter:drop-shadow(0 1px 1px rgba(0,0,0,0.2));"></div>`;
  const wrapperOpen = `<div style="position:relative;width:40px;height:48px;">`;
  const wrapperClose = `${tail}</div>`;

  if (imageUrl) {
    return L.divIcon({
      html: `${wrapperOpen}
               <div style="width:40px;height:40px;border-radius:50%;overflow:hidden;border:4px solid #ffffff;box-shadow:0 4px 10px rgba(0,0,0,0.3);background:#1e3a8a;">
                 <img src="${imageUrl}" style="width:100%;height:100%;object-fit:cover;display:block;" onerror="this.style.display='none'" />
               </div>
             ${wrapperClose}`,
      className: 'emoji-marker',
      iconSize: [40, 48],
      iconAnchor: [20, 48],
      popupAnchor: [0, -48]
    });
  }
  // Emoji fallback — circular white balloon with the emoji centered inside.
  return L.divIcon({
    html: `${wrapperOpen}
             <div style="width:40px;height:40px;border-radius:50%;background:#ffffff;border:4px solid #ffffff;box-shadow:0 4px 10px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;font-size:20px;line-height:1;">${emoji}</div>
           ${wrapperClose}`,
    className: 'emoji-marker',
    iconSize: [40, 48],
    iconAnchor: [20, 48],
    popupAnchor: [0, -48]
  });
};

// Map click handler component
const MapClickHandler = ({ onMapClick, isAddingMarker }) => {
  useMapEvents({
    click: (e) => {
      if (isAddingMarker) {
        onMapClick(e.latlng);
      }
    }
  });
  return null;
};

// =========================================================
// City-to-city Distance & Route data (used by the 📏 feature)
// Approximate real distances in km between Moroccan cities.
// Keys are city.name (case-sensitive, matches backend).
// =========================================================
const CITY_DISTANCES = {
  'Tangier-Rabat': 250, 'Tangier-Casablanca': 340, 'Tangier-Fez': 290, 'Tangier-Chefchaouen': 110,
  'Tangier-Marrakech': 580, 'Tangier-Agadir': 830, 'Tangier-Ouarzazate': 780, 'Tangier-Nador': 410, 'Tangier-Tiznit': 920,
  'Rabat-Casablanca': 90, 'Rabat-Fez': 200, 'Rabat-Chefchaouen': 200, 'Rabat-Marrakech': 320,
  'Rabat-Agadir': 580, 'Rabat-Ouarzazate': 525, 'Rabat-Nador': 470, 'Rabat-Tiznit': 670,
  'Casablanca-Fez': 290, 'Casablanca-Chefchaouen': 365, 'Casablanca-Marrakech': 240,
  'Casablanca-Agadir': 500, 'Casablanca-Ouarzazate': 440, 'Casablanca-Nador': 560, 'Casablanca-Tiznit': 590,
  'Fez-Chefchaouen': 200, 'Fez-Marrakech': 530, 'Fez-Agadir': 790, 'Fez-Ouarzazate': 670, 'Fez-Nador': 290, 'Fez-Tiznit': 880,
  'Chefchaouen-Marrakech': 600, 'Chefchaouen-Agadir': 860, 'Chefchaouen-Ouarzazate': 800, 'Chefchaouen-Nador': 300, 'Chefchaouen-Tiznit': 950,
  'Marrakech-Agadir': 250, 'Marrakech-Ouarzazate': 200, 'Marrakech-Nador': 820, 'Marrakech-Tiznit': 340,
  'Agadir-Ouarzazate': 370, 'Agadir-Nador': 1230, 'Agadir-Tiznit': 90,
  'Ouarzazate-Nador': 960, 'Ouarzazate-Tiznit': 460,
  'Nador-Tiznit': 1320,
};
// Cities that are on the ONCF rail network. Others fall back to "No direct train".
const ONCF_RAIL_CITIES = new Set(['Tangier', 'Rabat', 'Casablanca', 'Fez', 'Marrakech', 'Nador']);
const getCityDistanceKm = (a, b) => {
  if (!a || !b || a === b) return 0;
  return CITY_DISTANCES[`${a}-${b}`] ?? CITY_DISTANCES[`${b}-${a}`] ?? 0;
};
const _fmtDuration = (mins) => {
  if (mins < 60) return `${mins} min`;
  if (mins < 24 * 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h} h` : `${h} h ${m} m`;
  }
  return `~${Math.round(mins / (60 * 8))} days`;
};

// Map resize handler - fixes Leaflet gray tiles + re-invalidates when the map wrapper becomes
// visible after being hidden (e.g. user switches from Phrases tab to Markers tab). Without this,
// Leaflet measures the container as 0×0 during display:none and never recovers.
const MapResizeHandler = ({ activeTab, fullscreen }) => {
  const map = useMap();

  useEffect(() => {
    // Run on mount AND every time activeTab/fullscreen change.
    // Two timeouts: immediate (~50ms) for the layout to settle, plus a longer fallback (~300ms)
    // in case React/CSS transitions delay the actual size.
    const t1 = setTimeout(() => map.invalidateSize(), 50);
    const t2 = setTimeout(() => map.invalidateSize(), 300);

    const handleResize = () => map.invalidateSize();
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', handleResize);
    };
  }, [map, activeTab, fullscreen]);
  
  return null;
};

// City labels component
const CityLabels = ({ cities }) => {
  const map = useMap();
  
  useEffect(() => {
    const markers = cities.map(city => {
      const marker = L.marker([city.lat, city.lng], {
        icon: L.divIcon({
          html: `<div class="city-label">${city.name}</div>`,
          className: 'city-label-container',
          iconSize: [100, 20],
          iconAnchor: [50, 10]
        })
      });
      marker.addTo(map);
      return marker;
    });
    
    return () => {
      markers.forEach(m => map.removeLayer(m));
    };
  }, [cities, map]);
  
  return null;
};

const TravelTools = ({ isDarkMode, currentUser, userToken, hasPaidPlan, travelToolsSettings = {}, setCurrentView, setInitialProfileTab }) => {
  const token = userToken || localStorage.getItem('user_token');
  const { t } = useTranslation();
  const showProGlass = travelToolsSettings.is_pro_only && !hasPaidPlan;

  // App-shell caches: read synchronously so chrome (settings, cities, categories,
  // guide, markers, phrases) renders from frame 1 instead of the loading spinner.
  const _readMmCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  const _cachedMmSettings = _readMmCache('mmSettingsCache');
  const _cachedMmCities = _readMmCache('mmCitiesCache');
  const _cachedMmCategories = _readMmCache('mmCategoriesCache');
  const _cachedMmGuide = _readMmCache('mmGuideCache');
  const _cachedMmGuideCards = _readMmCache('mmGuideCardsCache');
  const _cachedMmMarkers = _readMmCache('mmMarkersCache');
  const _cachedMmPhrases = _readMmCache('mmPhrasesCache');
  const _cachedMmBudgetStats = _readMmCache('mmBudgetStatsCache');
  const _hasAnyMmCache = !!(_cachedMmSettings || _cachedMmCities || _cachedMmCategories);

  // State
  const [activeTab, setActiveTab] = useState('phrases'); // user lands on Phrases first (both mobile and desktop)
  // Custom confirmation dialog state (replaces ugly browser window.confirm)
  const [confirmDialog, setConfirmDialog] = useState(null); // {message, onYes}
  const [cities, setCities] = useState(_cachedMmCities || []);
  const [emojis, setEmojis] = useState([]);
  const [categories, setCategories] = useState(_cachedMmCategories || []);
  const [settings, setSettings] = useState(_cachedMmSettings || {});
  
  // User data
  const [markers, setMarkers] = useState(_cachedMmMarkers || []);
  const [phrases, setPhrases] = useState(_cachedMmPhrases || []);
  const [budgetEntries, setBudgetEntries] = useState([]);
  const [budgetStats, setBudgetStats] = useState(_cachedMmBudgetStats || { total: 0, by_category: {}, daily_budget: 0, total_budget: 0, remaining: 0, today_spent: 0, today_remaining: 0, over_daily_limit: false });
  const [budgetSettings, setBudgetSettings] = useState({ total_budget: 0, trip_days: 7, daily_budget: 0 });
  const [budgetCategories, setBudgetCategories] = useState([]);
  const [showBudgetSetup, setShowBudgetSetup] = useState(false);
  const [budgetView, setBudgetView] = useState('overview'); // 'overview' | 'expenses' | 'daily'

  // Widgets config (sidebar Prayer / Converter / Daily Goal / Weekly Progress) — fetched from backend.
  // Frontend always falls back to hardcoded defaults if the call fails so widgets never break.
  const _WIDGETS_FALLBACK = {
    prayer: {
      enabled: true, title: 'Prayer Times', subtitle: 'Today\'s 5 prayers',
      api_url: 'https://api.aladhan.com/v1/timingsByCity',
      method: 21, default_city: 'Marrakech',
      cities: ['Marrakech', 'Casablanca', 'Rabat', 'Fez', 'Tangier', 'Agadir', 'Essaouira', 'Chefchaouen', 'Meknes', 'Tetouan', 'Ouarzazate', 'Oujda', 'Nador', 'El Jadida', 'Asilah', 'Ifrane', 'Taroudant', 'Dakhla', 'Laayoune', 'Errachidia']
    },
    converter: {
      enabled: true, title: 'MAD Converter', subtitle: 'Quick foreign-currency math',
      api_urls: [
        'https://open.er-api.com/v6/latest/{BASE}',
        'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/{base}.json',
        'https://latest.currency-api.pages.dev/v1/currencies/{base}.json',
        'https://www.floatrates.com/daily/{base}.json'
      ],
      base_currency: 'MAD',
      currencies: ['EUR', 'USD', 'GBP', 'CAD', 'AUD', 'CHF', 'AED', 'SAR', 'JPY', 'CNY'],
      fallback_rates_per_mad: {
        EUR: 0.092, USD: 0.109, GBP: 0.081,
        CAD: 0.150, AUD: 0.151, CHF: 0.086,
        AED: 0.402, SAR: 0.408, JPY: 17.0, CNY: 0.79
      },
      disclaimer: 'Rates may vary — for reference only'
    },
    daily_goal: { enabled: true, title: 'Daily Goal', subtitle: 'Phrases to add today', default_goal: 3, min_goal: 1, max_goal: 20 },
    weekly_progress: { enabled: true, title: 'Weekly Progress', subtitle: 'Activity' }
  };
  // Seed widgets config from a localStorage cache so custom labels (e.g. prayer name "Duhr"
  // instead of "Dhuhr") show immediately on page load — avoids the brief flash of the
  // canonical name before the fetch returns. Cache is refreshed on every successful fetch.
  const _readWidgetsCache = () => {
    try {
      const raw = localStorage.getItem('mmWidgetsConfigCache');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return {
        prayer:           { ..._WIDGETS_FALLBACK.prayer,           ...(parsed.prayer || {}) },
        converter:        { ..._WIDGETS_FALLBACK.converter,        ...(parsed.converter || {}) },
        daily_goal:       { ..._WIDGETS_FALLBACK.daily_goal,       ...(parsed.daily_goal || {}) },
        weekly_progress:  { ..._WIDGETS_FALLBACK.weekly_progress,  ...(parsed.weekly_progress || {}) },
      };
    } catch { return null; }
  };
  const [widgetsConfig, setWidgetsConfig] = useState(_readWidgetsCache() || _WIDGETS_FALLBACK);

  // MAD Converter (sidebar widget) — rates fetched daily from a free FX API chain (see useEffect below).
  // Fallback rates are used only if all live sources fail. Cache key is versioned (_v2) so old
  // stale rates from an earlier API setup are automatically rejected on first load.
  const _FALLBACK_RATES_PER_1_MAD = {
    EUR: 0.092, USD: 0.109, GBP: 0.081,
    CAD: 0.150, AUD: 0.151, CHF: 0.086,
    AED: 0.402, SAR: 0.408, JPY: 17.0, CNY: 0.79
  };
  const _CONV_RATES_CACHE_KEY = 'mmConvRatesCache_v2';
  const _readConvRates = () => {
    try {
      const cached = localStorage.getItem(_CONV_RATES_CACHE_KEY);
      if (!cached) return null;
      const parsed = JSON.parse(cached);
      const ageHours = (Date.now() - (parsed.fetchedAt || 0)) / 3600000;
      if (ageHours > 24) return null;
      return parsed.rates;
    } catch { return null; }
  };
  // One-time cleanup of the pre-versioned cache key — silently remove old localStorage entry
  // so it can never be served. Safe no-op if the key doesn't exist.
  try { localStorage.removeItem('mmConvRatesCache'); } catch {}
  const [convRates, setConvRates] = useState(_readConvRates() || _FALLBACK_RATES_PER_1_MAD);
  const [convRatesLive, setConvRatesLive] = useState(!!_readConvRates());
  const [convAmount, setConvAmount] = useState('100');
  const [convFrom, setConvFrom] = useState('EUR'); // The non-MAD side of the conversion
  // Direction toggle: true = foreign→MAD (e.g. 100 EUR → 1088 MAD)
  //                  false = MAD→foreign (e.g. 100 MAD → 9.17 EUR)
  const [convForeignToMad, setConvForeignToMad] = useState(true);

  // Daily Goal — phrases to add today. Stored in localStorage so user's preference sticks.
  const _readDailyGoal = () => {
    try {
      const v = parseInt(localStorage.getItem('mmDailyGoal') || '3', 10);
      return Number.isFinite(v) && v > 0 && v < 100 ? v : 3;
    } catch { return 3; }
  };
  const [dailyGoal, setDailyGoal] = useState(_readDailyGoal());
  const _updateDailyGoal = (delta) => {
    setDailyGoal(prev => {
      const minG = widgetsConfig?.daily_goal?.min_goal ?? 1;
      const maxG = widgetsConfig?.daily_goal?.max_goal ?? 20;
      const next = Math.max(minG, Math.min(maxG, prev + delta));
      try { localStorage.setItem('mmDailyGoal', String(next)); } catch {}
      return next;
    });
  };

  // Prayer Times — cached per (city, date) so we don't refetch on every render. Method 21 = Morocco Habous official.
  const PRAYER_CITY_DEFAULT = 'Marrakech';
  const [prayerCity, setPrayerCity] = useState(() => {
    try { return localStorage.getItem('mmPrayerCity') || PRAYER_CITY_DEFAULT; }
    catch { return PRAYER_CITY_DEFAULT; }
  });
  const [prayerTimes, setPrayerTimes] = useState(null); // { Fajr, Dhuhr, Asr, Maghrib, Isha }
  const [prayerLoading, setPrayerLoading] = useState(false);
  // Prayer Times popup on Map (city-click)
  const [mapPrayerCity, setMapPrayerCity] = useState(null); // city name string or null when closed
  const [mapPrayerTimes, setMapPrayerTimes] = useState(null);

  // Minute tick — bumps state every 60s so the prayer card's "next" computation + countdown
  // refreshes as time passes (otherwise the IIFE never re-runs and the widget shows stale info).
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Helper: strip aladhan's "(+01)" timezone suffix so we display clean "13:32" instead of "13:32 (+01)".
  const _stripTzSuffix = (s) => (typeof s === 'string' ? s.replace(/\s*\([^)]+\)\s*$/, '').trim() : s);

  // Helper: compute "now" in a given IANA timezone as minutes-since-midnight (0..1439).
  // Falls back to Africa/Casablanca which is the right zone for our Morocco prayer use case.
  const _nowMinutesInTz = (timezone) => {
    try {
      const hhmm = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone || 'Africa/Casablanca',
        hour: '2-digit', minute: '2-digit', hour12: false
      }).format(new Date());
      const [h, m] = hhmm.split(':').map(n => parseInt(n, 10));
      return h * 60 + m;
    } catch {
      const now = new Date();
      return now.getHours() * 60 + now.getMinutes();
    }
  };
  
  // Shopping Lists data
  const [shoppingLists, setShoppingLists] = useState([]);
  const [currentList, setCurrentList] = useState(null);
  const [listItems, setListItems] = useState([]);
  const [shoppingStats, setShoppingStats] = useState(null);
  const [listCategories, setListCategories] = useState([]);
  const [shoppingView, setShoppingView] = useState('lists'); // 'lists' | 'stats' | 'wishlist'
  const [wishlistItems, setWishlistItems] = useState([]);
  // currencyRates is used by Budget Tracker + Shopping + PriceDisplay. We keep the state for
  // backwards compatibility, but it's now auto-synced from `convRates` (which has the 4-source
  // chain — open.er-api.com → fawazahmed0 jsDelivr → fawazahmed0 Cloudflare → floatrates). See
  // the useEffect below that mirrors convRates → currencyRates whenever the chain delivers fresh rates.
  const [currencyRates, setCurrencyRates] = useState({ USD: 0.10, EUR: 0.092 });
  const [units, setUnits] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  
  // Guide state
  const [guideData, setGuideData] = useState(_cachedMmGuide);
  // Cards shown on the public 📖 Guide tab (Emergency / Tipping / SIM / Cultural).
  // Cached in localStorage to avoid empty-state flash on every page load.
  const [guideCards, setGuideCards] = useState(_cachedMmGuideCards?.cards || []);
  const [expandedGuideCardId, setExpandedGuideCardId] = useState(null);
  
  // UI state
  const [isAddingMarker, setIsAddingMarker] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  // Skip the full-page spinner when any cache exists — chrome renders immediately
  // while a background fetch refreshes silently.
  const [loading, setLoading] = useState(!_hasAnyMmCache);
  const [showDeleteBudgetModal, setShowDeleteBudgetModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  // Small map-corner disclaimer popup — replaces the long yellow bar on desktop (looked ugly).
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  // 📋 Markers list panel — floating panel on the Map page showing all user markers with edit/delete
  const [markersPanelOpen, setMarkersPanelOpen] = useState(false);
  // 📱 Mobile widgets drawer — slides in from the right on mobile, contains the same 4 widgets
  // (Prayer / Weekly Progress / MAD Converter / Daily Goal) that live in the desktop sidebar.
  // Desktop is unaffected (the <aside> stays as a static column on lg+ screens).
  const [mobileWidgetsOpen, setMobileWidgetsOpen] = useState(false);

  // Lock scroll on BOTH <html> and <body> while the mobile widgets drawer is open so the page
  // behind cannot move. Body alone isn't enough — some apps scroll on <html> or a wrapper.
  // Only mobile; desktop never opens the drawer (it's a static sidebar instead).
  useEffect(() => {
    if (!mobileWidgetsOpen) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, [mobileWidgetsOpen]);
  // 📏 Distance & Routes feature — dropdown picks 2 cities, polyline draws between them, route card shows ETAs
  const [distancePanelOpen, setDistancePanelOpen] = useState(false);
  // When user closes the route card (✕), we just HIDE the card but keep the route line on the map.
  // Reset to false whenever From or To changes so a fresh route always shows its card.
  const [routeCardHidden, setRouteCardHidden] = useState(false);
  // Mobile-only — single collapsible route panel under zoom. Default expanded after picking cities;
  // tap the panel header to collapse to just an icon; tap the icon to expand again.
  const [selectedTransportMode, setSelectedTransportMode] = useState('car');
  const [routeInfoExpanded, setRouteInfoExpanded] = useState(true);
  // Multi-stop trip planner. Always at least 2 entries (min). Empty string = unselected slot.
  // Cap at 6 stops so the URL stays reasonable and the panel doesn't overwhelm the screen.
  const MAX_STOPS = 6;
  const [stops, setStops] = useState(['', '']);
  // Helpers — pure mutations on the stops array. removeStop is no-op when at min 2.
  const updateStop = (idx, name) => setStops(prev => prev.map((s, i) => (i === idx ? name : s)));
  const addStop    = () => setStops(prev => (prev.length < MAX_STOPS ? [...prev, ''] : prev));
  const removeStop = (idx) => setStops(prev => (prev.length > 2 ? prev.filter((_, i) => i !== idx) : prev));
  // Route fetched from OSRM:
  //   { coordinates: [[lat,lng],...], km, durationMin, legs: [{from,to,km,min}], stops: [name,...], source: 'osrm'|'fallback' }
  const [routeData, setRouteData] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);
  // Drag offsets for the floating cards — user can drag them around the map to see what's behind
  const [routeCardOffset, setRouteCardOffset] = useState({ x: 0, y: 0 });
  const [distPanelOffset, setDistPanelOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(null); // 'route' | 'panel' | null
  const dragStartRef = useRef({ x: 0, y: 0, oX: 0, oY: 0 });
  // New screen-based navigation (mobile): 'phrases' is the first screen the user sees, then they can switch to map/shopping/budget via the slide menu
  const [screen, setScreen] = useState('phrases');
  const [menuOpen, setMenuOpen] = useState(false);
  const selectScreen = (s) => {
    setScreen(s);
    setMenuOpen(false);
    if (s === 'map') {
      setActiveTab('markers');
    } else {
      setActiveTab(s);
    }
  };
  
  // Form states
  const [newMarker, setNewMarker] = useState({ emoji: '📍', title: '', content: '', category: '' });
  const [newPhrase, setNewPhrase] = useState({ english: '', darija: '', dialect: '', category: '', city_id: '' });
  const [newList, setNewList] = useState({ name: '', category: '' });
  const [newItem, setNewItem] = useState({ name: '', quantity: 1, unit: 'items', price: '', currency: 'MAD', note: '', city_id: '' });
  const [newBudget, setNewBudget] = useState({ name: '', amount: '', category: '', date: '', notes: '' });
  
  // Editing states
  const [editingPhrase, setEditingPhrase] = useState(null);
  const [editingList, setEditingList] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editingBudget, setEditingBudget] = useState(null);
  const [editingMarker, setEditingMarker] = useState(null);
  
  // Shopping UI state
  const [showCreateList, setShowCreateList] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [showAddItem, setShowAddItem] = useState(false);
  
  // Budget filter
  const [budgetPeriod, setBudgetPeriod] = useState('all');
  
  // Phrases UI state
  const [showAddPhrase, setShowAddPhrase] = useState(false);
  const [phraseSearch, setPhraseSearch] = useState('');
  const [phraseFilter, setPhraseFilter] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Delete confirmation modal
  const [deleteModal, setDeleteModal] = useState({ show: false, type: '', id: '', name: '' });
  
  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Free user: fetch demo data AFTER initial load (loading=false) and override
  // markers + phrases so the page shows something compelling behind the glass.
  // Gating on `loading` prevents loadUserData's empty result from overwriting demo.
  useEffect(() => {
    if (!showProGlass || loading) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/travel-tools/demo`);
        if (!res.ok || cancelled) return;
        const d = await res.json();
        if (Array.isArray(d.markers)) setMarkers(d.markers);
        if (Array.isArray(d.phrases)) setPhrases(d.phrases);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [showProGlass, loading, API]);
  
  const loadInitialData = async () => {
    // Only show spinner if no cached chrome — otherwise refresh silently
    if (!_hasAnyMmCache) setLoading(true);
    try {
      // Load public data including guide + guide cards
      const [citiesRes, emojisRes, categoriesRes, settingsRes, guideRes, guideCardsRes] = await Promise.all([
        fetch(`${API}/api/travel-tools/cities`),
        fetch(`${API}/api/travel-tools/emojis`),
        fetch(`${API}/api/travel-tools/categories`),
        fetch(`${API}/api/travel-tools/settings`),
        fetch(`${API}/api/travel-tools/guide`),
        fetch(`${API}/api/travel-tools/guide-cards`)
      ]);

      if (citiesRes.ok) { const d = await citiesRes.json(); setCities(d); try { localStorage.setItem('mmCitiesCache', JSON.stringify(d)); } catch {} }
      if (emojisRes.ok) setEmojis(await emojisRes.json());
      if (categoriesRes.ok) { const d = await categoriesRes.json(); setCategories(d); try { localStorage.setItem('mmCategoriesCache', JSON.stringify(d)); } catch {} }
      if (settingsRes.ok) { const d = await settingsRes.json(); setSettings(d); try { localStorage.setItem('mmSettingsCache', JSON.stringify(d)); } catch {} }
      if (guideRes.ok) { const d = await guideRes.json(); setGuideData(d); try { localStorage.setItem('mmGuideCache', JSON.stringify(d)); } catch {} }
      if (guideCardsRes.ok) { const d = await guideCardsRes.json(); setGuideCards(d.cards || []); try { localStorage.setItem('mmGuideCardsCache', JSON.stringify(d)); } catch {} }
      
      // Load user data if logged in
      if (token) {
        await loadUserData();
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };
  
  const loadUserData = async () => {
    try {
      const [markersRes, phrasesRes, listsRes, budgetRes, categoriesRes, currencyRes, unitsRes, templatesRes] = await Promise.all([
        fetch(`${API}/api/travel-tools/markers?token=${token}`),
        fetch(`${API}/api/travel-tools/phrases?token=${token}`),
        fetch(`${API}/api/travel-tools/shopping/lists?token=${token}`),
        fetch(`${API}/api/travel-tools/budget?token=${token}`),
        fetch(`${API}/api/travel-tools/shopping/categories`),
        fetch(`${API}/api/travel-tools/currency/rates`),
        fetch(`${API}/api/travel-tools/shopping/units`),
        fetch(`${API}/api/travel-tools/shopping/templates`)
      ]);
      
      if (markersRes.ok) { const d = await markersRes.json(); setMarkers(d); try { localStorage.setItem('mmMarkersCache', JSON.stringify(d)); } catch {} }
      if (phrasesRes.ok) { const d = await phrasesRes.json(); setPhrases(d); try { localStorage.setItem('mmPhrasesCache', JSON.stringify(d)); } catch {} }
      if (listsRes.ok) setShoppingLists(await listsRes.json());
      if (categoriesRes.ok) setListCategories(await categoriesRes.json());
      if (currencyRes.ok) {
        const currencyData = await currencyRes.json();
        setCurrencyRates(currencyData.rates);
      }
      if (unitsRes.ok) setUnits(await unitsRes.json());
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (budgetRes.ok) {
        const budgetData = await budgetRes.json();
        setBudgetEntries(budgetData.entries || []);
        setBudgetStats(budgetData);
        try { localStorage.setItem('mmBudgetStatsCache', JSON.stringify(budgetData)); } catch {}
      }
      
      // Load budget settings and categories
      const [budgetSettingsRes, budgetCatsRes] = await Promise.all([
        fetch(`${API}/api/travel-tools/budget/settings?token=${token}`),
        fetch(`${API}/api/travel-tools/budget/categories`)
      ]);
      if (budgetSettingsRes.ok) setBudgetSettings(await budgetSettingsRes.json());
      if (budgetCatsRes.ok) setBudgetCategories(await budgetCatsRes.json());
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };
  
  // Load shopping stats
  const loadShoppingStats = async () => {
    try {
      const res = await fetch(`${API}/api/travel-tools/shopping/stats?token=${token}`);
      if (res.ok) setShoppingStats(await res.json());
    } catch (error) {
      console.error('Error loading shopping stats:', error);
    }
  };
  
  // Load items for a list
  const loadListItems = async (listId) => {
    try {
      const res = await fetch(`${API}/api/travel-tools/shopping/lists/${listId}/items?token=${token}`);
      if (res.ok) {
        const data = await res.json();
        setCurrentList(data.list);
        setListItems(data.items || []);
      }
    } catch (error) {
      console.error('Error loading list items:', error);
    }
  };
  
  // Load wishlist
  const loadWishlist = async () => {
    try {
      const res = await fetch(`${API}/api/travel-tools/shopping/wishlist?token=${token}`);
      if (res.ok) setWishlistItems(await res.json());
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };
  
  // Load currency rates
  const loadCurrencyRates = async () => {
    try {
      const res = await fetch(`${API}/api/travel-tools/currency/rates`);
      if (res.ok) {
        const data = await res.json();
        setCurrencyRates(data.rates);
      }
    } catch (error) {
      console.error('Error loading currency rates:', error);
    }
  };
  
  // Currency converter helper
  const convertPrice = (priceDH) => {
    if (!priceDH || priceDH <= 0) return null;
    return {
      dh: priceDH,
      usd: (priceDH * currencyRates.USD).toFixed(2),
      eur: (priceDH * currencyRates.EUR).toFixed(2)
    };
  };
  
  // Map click handler
  const handleMapClick = useCallback((latlng) => {
    setSelectedPosition(latlng);
    setShowEmojiPicker(true);
    setNewMarker({ emoji: '📍', title: '', content: '', category: '' });
  }, []);
  
  // CRUD Operations - Markers
  const createMarker = async () => {
    if (!selectedPosition || !newMarker.title) return;
    
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('lat', selectedPosition.lat);
      formData.append('lng', selectedPosition.lng);
      formData.append('emoji', newMarker.emoji);
      formData.append('title', newMarker.title);
      formData.append('content', newMarker.content);
      if (newMarker.category) formData.append('category', newMarker.category);
      
      // Find nearest city
      const nearestCity = findNearestCity(selectedPosition);
      if (nearestCity) formData.append('city_id', nearestCity.id);
      
      const res = await fetch(`${API}/api/travel-tools/markers`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setMarkers([data.marker, ...markers]);
        setSelectedPosition(null);
        setShowEmojiPicker(false);
        setIsAddingMarker(false);
        setNewMarker({ emoji: '📍', title: '', content: '', category: '' });
      }
    } catch (error) {
      console.error('Error creating marker:', error);
    }
  };
  
  const updateMarker = async (markerId) => {
    if (!editingMarker) return;
    
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('emoji', editingMarker.emoji);
      formData.append('title', editingMarker.title);
      formData.append('content', editingMarker.content);
      if (editingMarker.category) formData.append('category', editingMarker.category);
      
      const res = await fetch(`${API}/api/travel-tools/markers/${markerId}`, { method: 'PUT', body: formData });
      if (res.ok) {
        setMarkers(markers.map(m => m.id === markerId ? { ...m, ...editingMarker } : m));
        setEditingMarker(null);
      }
    } catch (error) {
      console.error('Error updating marker:', error);
    }
  };
  
  const deleteMarker = (markerId) => {
    setConfirmDialog({
      message: 'Delete this marker?',
      onYes: async () => {
        try {
          const res = await fetch(`${API}/api/travel-tools/markers/${markerId}?token=${token}`, { method: 'DELETE' });
          if (res.ok) {
            setMarkers(markers.filter(m => m.id !== markerId));
          }
        } catch (error) {
          console.error('Error deleting marker:', error);
        }
      }
    });
  };
  
  // CRUD Operations - Phrases
  const createPhrase = async () => {
    if (!newPhrase.english || !newPhrase.darija) return;
    
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('text_english', newPhrase.english);
      formData.append('text_darija', newPhrase.darija);
      formData.append('text_third_lang', newPhrase.dialect || '');
      if (newPhrase.category) formData.append('category', newPhrase.category);
      if (newPhrase.city_id) formData.append('city_id', newPhrase.city_id);
      
      // Add custom fields
      const customFields = {};
      if (settings.phrase_fields) {
        settings.phrase_fields.forEach(field => {
          if (field.id.startsWith('custom_') && newPhrase[field.id]) {
            customFields[field.id] = newPhrase[field.id];
          }
        });
      }
      formData.append('custom_fields', JSON.stringify(customFields));
      
      const res = await fetch(`${API}/api/travel-tools/phrases`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setPhrases([data.phrase, ...phrases]);
        setNewPhrase({ english: '', darija: '', dialect: '', category: '', city_id: '' });
      }
    } catch (error) {
      console.error('Error creating phrase:', error);
    }
  };
  
  const updatePhrase = async (phraseId) => {
    if (!editingPhrase) return;
    
    try {
      const formData = new FormData();
      formData.append('token', token);
      
      // Add all dynamic fields from settings
      if (settings.phrase_fields) {
        settings.phrase_fields.forEach(field => {
          if (editingPhrase[field.id] !== undefined) {
            formData.append(field.id, editingPhrase[field.id] || '');
          }
        });
      }
      
      // Fallback for legacy fields if not in phrase_fields
      if (!settings.phrase_fields?.some(f => f.id === 'text_english')) {
        formData.append('text_english', editingPhrase.text_english || '');
      }
      if (!settings.phrase_fields?.some(f => f.id === 'text_darija')) {
        formData.append('text_darija', editingPhrase.text_darija || '');
      }
      if (!settings.phrase_fields?.some(f => f.id === 'text_third_lang')) {
        formData.append('text_third_lang', editingPhrase.text_third_lang || '');
      }
      
      if (editingPhrase.category) formData.append('category', editingPhrase.category);
      if (editingPhrase.city_id) formData.append('city_id', editingPhrase.city_id);
      
      // Add custom fields (for backward compatibility)
      const customFields = {};
      if (settings.phrase_fields) {
        settings.phrase_fields.forEach(field => {
          if (field.id.startsWith('custom_') && editingPhrase[field.id] !== undefined) {
            customFields[field.id] = editingPhrase[field.id];
          }
        });
      }
      formData.append('custom_fields', JSON.stringify(customFields));
      
      const res = await fetch(`${API}/api/travel-tools/phrases/${phraseId}`, { method: 'PUT', body: formData });
      if (res.ok) {
        setPhrases(phrases.map(p => p.id === phraseId ? { ...p, ...editingPhrase } : p));
        setEditingPhrase(null);
      }
    } catch (error) {
      console.error('Error updating phrase:', error);
    }
  };

  // Reorder a phrase within its category by swapping display_order with neighbor
  const movePhrase = async (phraseId, direction) => {
    const phrase = phrases.find(p => p.id === phraseId);
    if (!phrase) return;
    const sameCategory = [...phrases]
      .filter(p => (p.category || '') === (phrase.category || ''))
      .sort((a, b) => {
        const aOrder = a.display_order ?? 9999;
        const bOrder = b.display_order ?? 9999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (b.created_at || '').localeCompare(a.created_at || '');
      });
    const idx = sameCategory.findIndex(p => p.id === phraseId);
    const neighborIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (neighborIdx < 0 || neighborIdx >= sameCategory.length) return;
    const neighbor = sameCategory[neighborIdx];

    const myNewOrder = neighbor.display_order ?? neighborIdx;
    const neighborNewOrder = phrase.display_order ?? idx;

    // Optimistic update
    setPhrases(prev => prev.map(p => {
      if (p.id === phrase.id) return { ...p, display_order: myNewOrder };
      if (p.id === neighbor.id) return { ...p, display_order: neighborNewOrder };
      return p;
    }));

    // Persist both
    try {
      const swapBody = (newOrder) => {
        const fd = new FormData();
        fd.append('token', token);
        fd.append('display_order', String(newOrder));
        return fd;
      };
      await Promise.all([
        fetch(`${API}/api/travel-tools/phrases/${phrase.id}`, { method: 'PUT', body: swapBody(myNewOrder) }),
        fetch(`${API}/api/travel-tools/phrases/${neighbor.id}`, { method: 'PUT', body: swapBody(neighborNewOrder) }),
      ]);
    } catch (e) {
      console.error('Reorder failed', e);
    }
  };
  
  const deletePhrase = async (phraseId) => {
    try {
      const res = await fetch(`${API}/api/travel-tools/phrases/${phraseId}?token=${token}`, { method: 'DELETE' });
      if (res.ok) {
        setPhrases(phrases.filter(p => p.id !== phraseId));
        setDeleteModal({ show: false, type: '', id: '', name: '' });
      }
    } catch (error) {
      console.error('Error deleting phrase:', error);
    }
  };
  
  // Show delete confirmation modal
  const confirmDeletePhrase = (phrase) => {
    const displayName = phrase.text_english || phrase.text || 'this phrase';
    setDeleteModal({ show: true, type: 'phrase', id: phrase.id, name: displayName });
  };
  
  // CRUD Operations - Shopping Lists
  const createList = async () => {
    if (!newList.name) return;
    
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('name', newList.name);
      formData.append('category', newList.category);
      
      const res = await fetch(`${API}/api/travel-tools/shopping/lists`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setShoppingLists([{ ...data.list, total_items: 0, bought_items: 0, total_spent: 0 }, ...shoppingLists]);
        setNewList({ name: '', category: '' });
        setShowCreateList(false);
      }
    } catch (error) {
      console.error('Error creating list:', error);
    }
  };
  
  const deleteList = async (listId) => {
    try {
      const res = await fetch(`${API}/api/travel-tools/shopping/lists/${listId}?token=${token}`, { method: 'DELETE' });
      if (res.ok) {
        setShoppingLists(shoppingLists.filter(l => l.id !== listId));
        if (currentList?.id === listId) {
          setCurrentList(null);
          setListItems([]);
        }
        setDeleteModal({ show: false, type: '', id: '', name: '' });
      }
    } catch (error) {
      console.error('Error deleting list:', error);
    }
  };
  
  const confirmDeleteList = (list) => {
    setDeleteModal({ show: true, type: 'list', id: list.id, name: list.name });
  };
  
  // Create list from template
  const createListFromTemplate = async (templateId) => {
    try {
      const formData = new FormData();
      formData.append('token', token);
      
      const res = await fetch(`${API}/api/travel-tools/shopping/lists/from-template/${templateId}`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setShoppingLists([{ ...data.list, total_items: data.items_count, bought_items: 0, total_spent: 0 }, ...shoppingLists]);
        setShowTemplates(false);
        // Load the new list
        loadListItems(data.list.id);
      }
    } catch (error) {
      console.error('Error creating from template:', error);
    }
  };
  
  // CRUD Operations - Shopping Items
  const createItem = async () => {
    if (!newItem.name || !currentList) return;
    
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('name', newItem.name);
      formData.append('quantity', newItem.quantity || 1);
      formData.append('unit', newItem.unit || 'piece');
      formData.append('price', parseFloat(newItem.price) || 0);
      formData.append('note', newItem.note || '');
      if (newItem.city_id) formData.append('city_id', newItem.city_id);
      
      const res = await fetch(`${API}/api/travel-tools/shopping/lists/${currentList.id}/items`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setListItems([data.item, ...listItems]);
        setNewItem({ name: '', quantity: 1, unit: 'piece', price: '', note: '', city_id: '' });
        setShowAddItem(false);
        // Update list stats
        setShoppingLists(shoppingLists.map(l => 
          l.id === currentList.id 
            ? { ...l, total_items: l.total_items + 1 }
            : l
        ));
      }
    } catch (error) {
      console.error('Error creating item:', error);
    }
  };
  
  const toggleItemBought = async (itemId) => {
    try {
      const res = await fetch(`${API}/api/travel-tools/shopping/items/${itemId}/toggle?token=${token}`, { method: 'PUT' });
      if (res.ok) {
        const data = await res.json();
        setListItems(listItems.map(i => i.id === itemId ? { ...i, is_bought: data.is_bought } : i));
        // Update list stats
        const item = listItems.find(i => i.id === itemId);
        if (item && currentList) {
          const delta = data.is_bought ? 1 : -1;
          const priceDelta = data.is_bought ? (item.price || 0) : -(item.price || 0);
          setShoppingLists(shoppingLists.map(l => 
            l.id === currentList.id 
              ? { ...l, bought_items: l.bought_items + delta, total_spent: l.total_spent + priceDelta }
              : l
          ));
        }
      }
    } catch (error) {
      console.error('Error toggling item:', error);
    }
  };
  
  const updateItem = async (itemId) => {
    if (!editingItem) return;
    
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('name', editingItem.name);
      formData.append('quantity', editingItem.quantity || 1);
      formData.append('price', parseFloat(editingItem.price) || 0);
      formData.append('note', editingItem.note || '');
      if (editingItem.city_id) formData.append('city_id', editingItem.city_id);
      
      const res = await fetch(`${API}/api/travel-tools/shopping/items/${itemId}`, { method: 'PUT', body: formData });
      if (res.ok) {
        setListItems(listItems.map(i => i.id === itemId ? { ...i, ...editingItem } : i));
        setEditingItem(null);
      }
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };
  
  const deleteItem = async (itemId) => {
    try {
      const res = await fetch(`${API}/api/travel-tools/shopping/items/${itemId}?token=${token}`, { method: 'DELETE' });
      if (res.ok) {
        const item = listItems.find(i => i.id === itemId);
        setListItems(listItems.filter(i => i.id !== itemId));
        // Update list stats
        if (item && currentList) {
          const boughtDelta = item.is_bought ? -1 : 0;
          const priceDelta = item.is_bought ? -(item.price || 0) : 0;
          setShoppingLists(shoppingLists.map(l => 
            l.id === currentList.id 
              ? { ...l, total_items: l.total_items - 1, bought_items: l.bought_items + boughtDelta, total_spent: l.total_spent + priceDelta }
              : l
          ));
        }
        setDeleteModal({ show: false, type: '', id: '', name: '' });
      }
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };
  
  const confirmDeleteItem = (item) => {
    setDeleteModal({ show: true, type: 'item', id: item.id, name: item.name });
  };
  
  // Wishlist state for new item
  const [newWishItem, setNewWishItem] = useState({ name: '', target_price: '', note: '', city_id: '' });
  const [showAddWish, setShowAddWish] = useState(false);
  
  // CRUD Operations - Wishlist
  const addToWishlist = async () => {
    if (!newWishItem.name) return;
    
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('name', newWishItem.name);
      formData.append('target_price', parseFloat(newWishItem.target_price) || 0);
      formData.append('note', newWishItem.note || '');
      if (newWishItem.city_id) formData.append('city_id', newWishItem.city_id);
      
      const res = await fetch(`${API}/api/travel-tools/shopping/wishlist`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setWishlistItems([data.item, ...wishlistItems]);
        setNewWishItem({ name: '', target_price: '', note: '', city_id: '' });
        setShowAddWish(false);
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
    }
  };
  
  const markWishPurchased = async (itemId, price, cityId) => {
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('purchased_price', price);
      if (cityId) formData.append('purchased_city', cityId);
      
      const res = await fetch(`${API}/api/travel-tools/shopping/wishlist/${itemId}/purchase`, { method: 'PUT', body: formData });
      if (res.ok) {
        setWishlistItems(wishlistItems.map(i => 
          i.id === itemId 
            ? { ...i, is_purchased: true, purchased_price: price, purchased_city: cityId }
            : i
        ));
      }
    } catch (error) {
      console.error('Error marking as purchased:', error);
    }
  };
  
  const deleteWishItem = async (itemId) => {
    try {
      const res = await fetch(`${API}/api/travel-tools/shopping/wishlist/${itemId}?token=${token}`, { method: 'DELETE' });
      if (res.ok) {
        setWishlistItems(wishlistItems.filter(i => i.id !== itemId));
        setDeleteModal({ show: false, type: '', id: '', name: '' });
      }
    } catch (error) {
      console.error('Error deleting wishlist item:', error);
    }
  };
  
  const confirmDeleteWish = (item) => {
    setDeleteModal({ show: true, type: 'wish', id: item.id, name: item.name });
  };
  
  // CRUD Operations - Budget
  const createBudgetEntry = async () => {
    if (!newBudget.name || !newBudget.amount) return;
    
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('name', newBudget.name);
      formData.append('amount', parseFloat(newBudget.amount));
      if (newBudget.category) formData.append('category', newBudget.category);
      if (newBudget.date) formData.append('date', newBudget.date);
      formData.append('notes', newBudget.notes);
      
      const res = await fetch(`${API}/api/travel-tools/budget`, { method: 'POST', body: formData });
      if (res.ok) {
        await loadBudgetData();
        setNewBudget({ name: '', amount: '', category: '', date: '', notes: '' });
      }
    } catch (error) {
      console.error('Error creating budget entry:', error);
    }
  };
  
  const loadBudgetData = async () => {
    try {
      const url = budgetPeriod === 'all' 
        ? `${API}/api/travel-tools/budget?token=${token}`
        : `${API}/api/travel-tools/budget?token=${token}&period=${budgetPeriod}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setBudgetEntries(data.entries || []);
        setBudgetStats(data);
        // Only cache the unfiltered 'all' view so the stat card always reflects the trip total
        if (budgetPeriod === 'all') {
          try { localStorage.setItem('mmBudgetStatsCache', JSON.stringify(data)); } catch {}
        }
      }
    } catch (error) {
      console.error('Error loading budget:', error);
    }
  };
  
  const saveBudgetSettings = async (totalBudget, tripDays) => {
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('total_budget', totalBudget);
      formData.append('trip_days', tripDays);
      
      const res = await fetch(`${API}/api/travel-tools/budget/settings`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setBudgetSettings(data.settings);
        setShowBudgetSetup(false);
        loadBudgetData();
      }
    } catch (error) {
      console.error('Error saving budget settings:', error);
    }
  };
  
  // Delete/reset budget settings
  const deleteBudgetSettings = async () => {
    try {
      const res = await fetch(`${API}/api/travel-tools/budget/settings?token=${token}`, { method: 'DELETE' });
      if (res.ok) {
        setBudgetSettings({ total_budget: 0, trip_days: 7, daily_budget: 0 });
        setShowBudgetSetup(true);
        loadBudgetData();
      }
    } catch (error) {
      console.error('Error deleting budget settings:', error);
    }
  };
  
  useEffect(() => {
    if (token && activeTab === 'budget') {
      loadBudgetData();
    }
  }, [budgetPeriod, activeTab]);

  // 💱 Mirror live FX rates from the MAD Converter chain (convRates) into the older `currencyRates`
  // state that Budget / Shopping / PriceDisplay components read. This means a single fetch (the
  // converter's 4-source chain) powers every "$X / €Y" conversion in the app — no separate fetches.
  // Only sync once convRates is "live" so we don't overwrite Budget's reasonable fallback with
  // identical fallback values.
  useEffect(() => {
    if (!convRatesLive) return;
    const next = {};
    if (typeof convRates.USD === 'number') next.USD = convRates.USD;
    if (typeof convRates.EUR === 'number') next.EUR = convRates.EUR;
    if (Object.keys(next).length) setCurrencyRates(prev => ({ ...prev, ...next }));
  }, [convRates, convRatesLive]);

  // 💱 MAD Converter — fetch live FX rates once per 24h.
  // Tries each URL in `api_urls` (admin-configurable). First success wins. If all fail,
  // hardcoded fallback rates stay in effect. Placeholders:
  //   {BASE} → uppercase base (e.g. MAD), used in path or query
  //   {base} → lowercase base (e.g. mad), used by fawazahmed0/currency-api
  // Response shape is auto-detected — supports `data.rates` (open.er-api.com style) and
  // `data[base.toLowerCase()]` (fawazahmed0 style).
  useEffect(() => {
    if (convRatesLive) return; // cache is fresh
    const convCfg = widgetsConfig.converter || _WIDGETS_FALLBACK.converter;
    // Source of truth for the API list. Backwards-compat: if a doc still has old single `api_url`,
    // promote it to the front of the chain. exchangerate.host (now paywalled) is silently dropped.
    let apiUrls = (Array.isArray(convCfg.api_urls) && convCfg.api_urls.length > 0)
      ? convCfg.api_urls.slice()
      : (convCfg.api_url ? [convCfg.api_url] : _WIDGETS_FALLBACK.converter.api_urls.slice());
    apiUrls = apiUrls.filter(u => typeof u === 'string' && u.trim() && !u.includes('api.exchangerate.host'));
    if (apiUrls.length === 0) apiUrls = _WIDGETS_FALLBACK.converter.api_urls.slice();
    const base = convCfg.base_currency || 'MAD';
    const baseLower = base.toLowerCase();
    const currencies = convCfg.currencies || ['EUR', 'USD', 'GBP'];
    const symbols = currencies.join(',');

    // Substitute placeholders + handle non-placeholder URLs (append query params)
    const buildUrl = (template) => {
      if (template.includes('{BASE}') || template.includes('{base}') || template.includes('{SYMBOLS}')) {
        return template
          .replace('{BASE}', encodeURIComponent(base))
          .replace('{base}', encodeURIComponent(baseLower))
          .replace('{SYMBOLS}', encodeURIComponent(symbols));
      }
      const sep = template.includes('?') ? '&' : '?';
      return `${template}${sep}base=${base}&symbols=${symbols}`;
    };

    // Try to pull a flat rates object out of the response no matter which API it came from.
    // Possible shapes:
    //   1. open.er-api.com / exchangerate.host: { rates: { EUR: 0.092, USD: 0.109, ... } }
    //   2. fawazahmed0/currency-api:           { date: "...", mad: { eur: 0.092, usd: 0.109, ... } }
    //   3. floatrates.com:                     { usd: { code: "USD", rate: 0.109, ... }, eur: {...} }
    const extractRates = (data) => {
      if (!data) return null;
      if (data.result && data.result !== 'success') return null;
      // Shape 1
      if (data.rates && typeof data.rates === 'object') return data.rates;
      // Shape 2
      if (data[baseLower] && typeof data[baseLower] === 'object' && typeof Object.values(data[baseLower])[0] === 'number') {
        return data[baseLower];
      }
      if (data[base] && typeof data[base] === 'object' && typeof Object.values(data[base])[0] === 'number') {
        return data[base];
      }
      // Shape 3 — top-level keys are currency codes; values are objects with `.rate`
      const firstVal = Object.values(data)[0];
      if (firstVal && typeof firstVal === 'object' && typeof firstVal.rate === 'number') {
        const flat = {};
        for (const [k, v] of Object.entries(data)) {
          if (v && typeof v === 'object' && typeof v.rate === 'number') flat[k] = v.rate;
        }
        return flat;
      }
      return null;
    };

    let aborted = false;
    (async () => {
      for (const tmpl of apiUrls) {
        if (aborted) return;
        const url = buildUrl(tmpl);
        try {
          const res = await fetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          if (aborted) return;
          const ratesObj = extractRates(data);
          if (!ratesObj) throw new Error('no rates in response');
          // Currency codes may be UPPER (open.er-api) or lower (fawazahmed0) — try both.
          const rates = {};
          currencies.forEach(c => {
            const v = ratesObj[c] ?? ratesObj[c.toLowerCase()] ?? ratesObj[c.toUpperCase()];
            if (typeof v === 'number') rates[c] = v;
          });
          if (!Object.keys(rates).length) throw new Error('no matching currencies');
          // SUCCESS — stop trying further URLs
          console.log('[MAD Converter] live rates from:', new URL(url).hostname, rates);
          setConvRates(rates);
          setConvRatesLive(true);
          try { localStorage.setItem(_CONV_RATES_CACHE_KEY, JSON.stringify({ rates, fetchedAt: Date.now() })); } catch {}
          return;
        } catch (err) {
          console.warn('[MAD Converter] FX source failed:', new URL(url).hostname, '·', err?.message || err);
        }
      }
      // All sources exhausted — leave fallback rates in place.
      console.warn('[MAD Converter] all FX sources failed, using hardcoded fallback rates');
    })();
    return () => { aborted = true; };
  }, [convRatesLive, widgetsConfig.converter]);

  // 🧩 Sync prayer city to admin default_city when widgets config arrives, ONLY if user hasn't picked a city before.
  useEffect(() => {
    try {
      if (localStorage.getItem('mmPrayerCity')) return; // user already chose; respect that
    } catch {}
    const adminDefault = widgetsConfig?.prayer?.default_city;
    if (adminDefault && adminDefault !== prayerCity) {
      setPrayerCity(adminDefault);
    }
  }, [widgetsConfig.prayer?.default_city]);

  // 🧩 Widgets config — fetched once on mount. Merged into fallback so a partial admin doc never breaks the page.
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/travel-tools/widgets-config`);
        if (!res.ok) return;
        const data = await res.json();
        if (aborted || !data) return;
        // Shallow-merge each section so missing keys keep their fallback values
        const merged = {
          prayer:           { ..._WIDGETS_FALLBACK.prayer,           ...(data.prayer || {}) },
          converter:        { ..._WIDGETS_FALLBACK.converter,        ...(data.converter || {}) },
          daily_goal:       { ..._WIDGETS_FALLBACK.daily_goal,       ...(data.daily_goal || {}) },
          weekly_progress:  { ..._WIDGETS_FALLBACK.weekly_progress,  ...(data.weekly_progress || {}) }
        };
        setWidgetsConfig(merged);
        // Cache so the next page load shows custom labels immediately (no Dhuhr→Duhr flash).
        try { localStorage.setItem('mmWidgetsConfigCache', JSON.stringify(merged)); } catch {}
      } catch {/* keep fallback */}
    })();
    return () => { aborted = true; };
  }, []);

  // 🕌 Prayer Times — fetch for sidebar city. URL + method pulled from admin config (with hardcoded fallback).
  // Cache key bumped to _v2_ because the stored shape changed (now includes timezone).
  useEffect(() => {
    if (!prayerCity) return;
    const prayerCfg = widgetsConfig.prayer || {};
    // Always have a usable URL — fall back to hardcoded default if admin cleared/missed the field.
    const apiUrl = prayerCfg.api_url || _WIDGETS_FALLBACK.prayer.api_url;
    const dateKey = new Date().toISOString().split('T')[0];
    const cacheKey = `mmPrayerTimes_v2_${prayerCity}_${dateKey}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.timings) { setPrayerTimes(parsed); return; }
      }
    } catch {}
    let aborted = false;
    setPrayerLoading(true);
    (async () => {
      try {
        const method = prayerCfg.method ?? 21;
        const sep = apiUrl.includes('?') ? '&' : '?';
        const url = `${apiUrl}${sep}city=${encodeURIComponent(prayerCity)}&country=Morocco&method=${method}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (aborted) return;
        const t = data?.data?.timings;
        if (!t) throw new Error('no timings in response');
        const payload = {
          timings: { Fajr: t.Fajr, Dhuhr: t.Dhuhr, Asr: t.Asr, Maghrib: t.Maghrib, Isha: t.Isha },
          timezone: data?.data?.meta?.timezone || 'Africa/Casablanca'
        };
        setPrayerTimes(payload);
        try { localStorage.setItem(cacheKey, JSON.stringify(payload)); } catch {}
      } catch (err) {
        console.warn('[Prayer Times] fetch failed:', err?.message || err);
        if (!aborted) setPrayerTimes(null);
      } finally {
        if (!aborted) setPrayerLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [prayerCity, widgetsConfig.prayer]);

  // 🕌 Prayer Times — fetch for whichever city the user clicked on the map (separate from sidebar city)
  useEffect(() => {
    if (!mapPrayerCity) { setMapPrayerTimes(null); return; }
    const prayerCfg = widgetsConfig.prayer || {};
    const apiUrl = prayerCfg.api_url || _WIDGETS_FALLBACK.prayer.api_url;
    const dateKey = new Date().toISOString().split('T')[0];
    const cacheKey = `mmPrayerTimes_v2_${mapPrayerCity}_${dateKey}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.timings) { setMapPrayerTimes(parsed); return; }
      }
    } catch {}
    let aborted = false;
    (async () => {
      try {
        const method = prayerCfg.method ?? 21;
        const sep = apiUrl.includes('?') ? '&' : '?';
        const url = `${apiUrl}${sep}city=${encodeURIComponent(mapPrayerCity)}&country=Morocco&method=${method}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (aborted) return;
        const t = data?.data?.timings;
        if (!t) throw new Error('no timings in response');
        const payload = {
          timings: { Fajr: t.Fajr, Dhuhr: t.Dhuhr, Asr: t.Asr, Maghrib: t.Maghrib, Isha: t.Isha },
          timezone: data?.data?.meta?.timezone || 'Africa/Casablanca'
        };
        setMapPrayerTimes(payload);
        try { localStorage.setItem(cacheKey, JSON.stringify(payload)); } catch {}
      } catch (err) {
        console.warn('[Prayer Times Map] fetch failed:', err?.message || err);
        if (!aborted) setMapPrayerTimes(null);
      }
    })();
    return () => { aborted = true; };
  }, [mapPrayerCity, widgetsConfig.prayer]);

  // 🤏 Drag handlers — move the floating Route card / Distance panel around the map
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const clientX = e.clientX ?? e.touches?.[0]?.clientX;
      const clientY = e.clientY ?? e.touches?.[0]?.clientY;
      if (clientX == null) return;
      const next = {
        x: dragStartRef.current.oX + (clientX - dragStartRef.current.x),
        y: dragStartRef.current.oY + (clientY - dragStartRef.current.y),
      };
      if (dragging === 'route') setRouteCardOffset(next);
      else if (dragging === 'panel') setDistPanelOffset(next);
    };
    const onEnd = () => setDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [dragging]);

  const startDrag = (which, offset, e) => {
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX == null) return;
    dragStartRef.current = { x: clientX, y: clientY, oX: offset.x, oY: offset.y };
    setDragging(which);
    // Only call preventDefault for mouse events. React's touchstart is passive in modern browsers,
    // so calling preventDefault inside it throws a warning AND fails silently — worse, it can leave
    // the touch handler in an ambiguous state where the tap also reaches the underlying map (Leaflet
    // interprets it as a pan/zoom). Skipping preventDefault for touch lets the drag start cleanly.
    if (e.type === 'mousedown') {
      try { e.preventDefault?.(); } catch {}
    }
  };

  // 📏 Fetch real driving route from OSRM (free public server) when 2 different cities are picked.
  // Returns real road distance + duration + path geometry that follows roads (gives the "nice curve" look).
  // Falls back to direct line + hardcoded matrix if the API is unreachable.
  // Route is computed ONLY when the user clicks the Go button — never automatically on stop change.
  // We track the latest in-flight request with a token so clicks during a previous fetch cancel it.
  const routeFetchTokenRef = useRef(0);
  const computeRoute = () => {
    // Need ≥2 stops, all selected, and no consecutive duplicates (A→A in a row is invalid).
    const filled = stops.filter(Boolean);
    if (filled.length < 2 || filled.some((s, i) => i > 0 && s === filled[i - 1])) {
      return;
    }
    // Look up coords for each stop; if any city is unknown, bail silently (button is disabled in that case).
    const coords = filled.map(name => cities.find(c => c.name === name));
    if (coords.some(c => !c)) return;

    // Fresh request token — any previous in-flight fetch will be ignored when it resolves.
    const myToken = ++routeFetchTokenRef.current;
    // Reset card UI state for a fresh route
    setRouteCardHidden(false);
    setSelectedTransportMode('car');
    setRouteInfoExpanded(true);
    setRouteLoading(true);

    // OSRM expects lon,lat. Public demo server, no API key.
    // Multi-stop: semicolon-separated coords. One call returns the full polyline + per-leg breakdown.
    const waypoints = coords.map(c => `${c.lng},${c.lat}`).join(';');
    const url = `https://router.project-osrm.org/route/v1/driving/${waypoints}?overview=full&geometries=geojson&steps=false`;
    fetch(url)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        if (myToken !== routeFetchTokenRef.current) return; // stale
        const route = data?.routes?.[0];
        if (!route) throw new Error('no route');
        const coordinates = route.geometry.coordinates.map(([lon, lat]) => [lat, lon]);
        const legs = (route.legs || []).map((leg, i) => ({
          from: filled[i],
          to:   filled[i + 1],
          km:   Math.round(leg.distance / 1000),
          min:  Math.round(leg.duration / 60),
        }));
        setRouteData({
          coordinates,
          km: Math.round(route.distance / 1000),
          durationMin: Math.round(route.duration / 60),
          legs,
          stops: filled,
          source: 'osrm',
        });
      })
      .catch(() => {
        if (myToken !== routeFetchTokenRef.current) return; // stale
        // Fallback — straight-line series + hardcoded matrix summed across consecutive pairs.
        let totalKm = 0;
        const legs = [];
        let ok = true;
        for (let i = 0; i < filled.length - 1; i++) {
          const segKm = getCityDistanceKm(filled[i], filled[i + 1]);
          if (segKm <= 0) { ok = false; break; }
          const segKmAdj = Math.round(segKm * 1.3);
          const segMin   = Math.round(segKmAdj / 80 * 60);
          legs.push({ from: filled[i], to: filled[i + 1], km: segKmAdj, min: segMin });
          totalKm += segKmAdj;
        }
        if (ok && totalKm > 0) {
          setRouteData({
            coordinates: coords.map(c => [c.lat, c.lng]),
            km: totalKm,
            durationMin: legs.reduce((s, l) => s + l.min, 0),
            legs,
            stops: filled,
            source: 'fallback',
          });
        } else {
          setRouteData(null);
        }
      })
      .finally(() => { if (myToken === routeFetchTokenRef.current) setRouteLoading(false); });
  };
  
  const updateBudgetEntry = async (entryId) => {
    if (!editingBudget) return;
    
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('name', editingBudget.name);
      formData.append('amount', parseFloat(editingBudget.amount));
      if (editingBudget.category) formData.append('category', editingBudget.category);
      if (editingBudget.date) formData.append('date', editingBudget.date);
      formData.append('notes', editingBudget.notes);
      
      const res = await fetch(`${API}/api/travel-tools/budget/${entryId}`, { method: 'PUT', body: formData });
      if (res.ok) {
        await loadBudgetData();
        setEditingBudget(null);
      }
    } catch (error) {
      console.error('Error updating budget entry:', error);
    }
  };
  
  const deleteBudgetEntry = (entryId) => {
    setConfirmDialog({
      message: 'Delete this entry?',
      onYes: async () => {
        try {
          const res = await fetch(`${API}/api/travel-tools/budget/${entryId}?token=${token}`, { method: 'DELETE' });
          if (res.ok) {
            await loadBudgetData();
          }
        } catch (error) {
          console.error('Error deleting budget entry:', error);
        }
      }
    });
  };
  
  // Helper functions
  // Stored marker key is either the emoji char (e.g. "🐪") OR a sentinel "__id:<id>" when the
  // admin entry is image-only with no emoji char. Render-time lookup matches both forms.
  const findEmojiImageUrl = (key) => {
    if (!key) return '';
    if (key.startsWith && key.startsWith('__id:')) {
      const id = key.slice(5);
      const match = emojis.find(e => e.id === id);
      return (match && match.image_url) || '';
    }
    const match = emojis.find(e => e.emoji === key);
    return (match && match.image_url) || '';
  };
  // Picker key for an admin emoji entry — what gets stored in marker.emoji
  const pickerKeyOf = (e) => e.emoji || `__id:${e.id}`;
  // Clean display char for fallback rendering — hide sentinel from users
  const displayEmojiChar = (key) => (key && key.startsWith && key.startsWith('__id:')) ? '📍' : (key || '📍');

  const findNearestCity = (position) => {
    let nearest = null;
    let minDist = Infinity;
    cities.forEach(city => {
      const dist = Math.sqrt(Math.pow(city.lat - position.lat, 2) + Math.pow(city.lng - position.lng, 2));
      if (dist < minDist) {
        minDist = dist;
        nearest = city;
      }
    });
    return nearest;
  };
  
  const getCityName = (cityId) => {
    const city = cities.find(c => c.id === cityId);
    return city ? city.name : '';
  };
  
  const getCategoryName = (categoryId, type) => {
    const cat = categories.find(c => c.id === categoryId && c.type === type);
    return cat ? cat.name : categoryId;
  };
  
  // Render loading
  if (loading) {
    return (
      <div className={`flex items-center justify-center h-full ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }
  
  // Render login prompt
  if (!token) {
    return (
      <div className={`flex flex-col items-center justify-center h-full p-8 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
        <div className="text-6xl mb-4">🇲🇦</div>
        <h2 className="text-2xl font-bold mb-2">{t('my_morocco')}</h2>
        <p className={`text-center mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {t('morocco_login_prompt')}
        </p>
        <a href="/login" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          {t('morocco_login_button')}
        </a>
      </div>
    );
  }
  
  return (
    <div className={`h-full min-h-screen flex flex-col relative ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header — sticky on mobile, subtle gradient backdrop */}
      <div className={`sticky top-0 z-30 px-4 py-3 border-b backdrop-blur-md sm:backdrop-blur-none ${isDarkMode ? 'bg-gray-800/95 border-gray-700' : 'bg-gradient-to-r from-blue-50/95 via-white/95 to-purple-50/95 sm:bg-white sm:from-transparent sm:via-transparent sm:to-transparent border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Mobile-only back arrow next to the heading. Uses the EXACT same nav pattern as the
                existing ModernLayout sidebar (push '/translation' first, then setCurrentView). */}
            <button
              onClick={() => {
                // Restore body scroll BEFORE unmounting (in case the widgets drawer was open
                // — its useEffect cleanup runs but only after React commits the new view).
                try {
                  document.documentElement.style.overflow = '';
                  document.body.style.overflow = '';
                } catch {}
                if (setCurrentView) {
                  try { window.history.pushState({}, '', '/translation'); } catch {}
                  setCurrentView('translator');
                } else {
                  try { window.history.back(); } catch {}
                }
              }}
              className={`lg:hidden w-8 h-8 rounded-full flex items-center justify-center text-base font-bold transition-colors ${
                isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              aria-label={t('morocco_back_to_home')}
              title={t('morocco_back_to_home')}
            >
              ‹
            </button>
            <span className="text-xl sm:text-lg">🇲🇦</span>
            <h1 className={`text-lg sm:text-base font-bold sm:font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('my_morocco')}</h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Old green "📍 Add" desktop button removed — the floating map header has its own
                "+ Marker" button, so the page-header add was duplicate. */}
            <button
              onClick={() => setShowHelpModal(true)}
              className={`w-8 h-8 sm:w-5 sm:h-5 rounded-full flex items-center justify-center text-sm sm:text-xs font-bold transition-colors ${
                isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
              title={t('morocco_help_tooltip')}
            >
              ?
            </button>
          </div>
        </div>
        {isAddingMarker && (
          <p className={`text-sm mt-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
            👆 {t('morocco_add_note_hint_long')}
          </p>
        )}
      </div>
      
      {/* Left ☰ menu button removed — the tab bar at the top of the page now provides navigation
          (📝 Phrases / 🛒 Shopping / 💰 Budget / 📍 Markers), making the left slide-out menu redundant. */}

      {/* Widgets drawer launcher — MOBILE ONLY, bottom-left, above the sticky mobile bottom nav.
          Uses an inline SVG (4-squares grid) instead of an emoji so the icon looks crisp at any size. */}
      <button
        onClick={() => setMobileWidgetsOpen(true)}
        className="lg:hidden fixed left-4 bottom-20 z-40 w-12 h-12 rounded-full bg-slate-900 text-white shadow-xl flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
        aria-label={t('morocco_open_widgets')}
        title={t('morocco_widgets_tooltip')}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <rect x="3"  y="3"  width="7" height="7" rx="1.5" />
          <rect x="14" y="3"  width="7" height="7" rx="1.5" />
          <rect x="3"  y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
      </button>

      {/* Backdrop for the mobile widgets drawer */}
      {mobileWidgetsOpen && (
        <div
          onClick={() => setMobileWidgetsOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/50 z-[1400] backdrop-blur-sm"
          aria-label={t('morocco_close_widgets')}
        />
      )}

      {/* Old slide-in left menu removed — the page tab bar (📝 Phrases / 🛒 Shopping / 💰 Budget / 📍 Markers)
          handles navigation on mobile now. `menuOpen` state is still declared upstream but unused;
          left here as a harmless no-op rather than ripple removal across other call-sites. */}

      {/* Main Content — single-column. Tab bar always visible. Map is its own page; Phrases/Shop/Budget share an Activity sidebar. */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Tab pill bar — visible on ALL sizes now. Mobile: buttons fill width equally with compact
            padding/text so all 4 fit. Desktop: lg: overrides restore the original natural-width
            buttons + larger padding/text. Desktop output is unchanged. */}
        <div className={`flex items-center justify-between px-3 lg:px-5 py-2 lg:py-3 shrink-0 ${isDarkMode ? 'bg-gray-900' : 'bg-slate-50'}`}>
          <div className={`flex gap-0.5 lg:gap-1 p-1 rounded-2xl shadow-sm flex-1 lg:flex-initial ${isDarkMode ? 'bg-gray-800' : 'bg-white border border-gray-100'}`}>
            {[
              { id: 'phrases', icon: '📝', labelKey: 'morocco_tab_phrases' },
              { id: 'shopping', icon: '🛒', labelKey: 'morocco_tab_shopping' },
              { id: 'budget', icon: '💰', labelKey: 'morocco_tab_budget' },
              // Tab id stays 'markers' for backwards-compat with state/screen logic; the
              // displayed icon + label are now "🗺️ Map" since this tab IS the map page.
              { id: 'markers', icon: '🗺️', labelKey: 'morocco_map_label' },
              { id: 'guide', icon: '📖', labelKey: 'morocco_tab_guide' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setScreen(tab.id === 'markers' ? 'map' : tab.id); }}
                className={`flex-1 lg:flex-initial px-1.5 lg:px-4 py-1.5 lg:py-2 rounded-xl text-[11px] lg:text-sm font-bold flex items-center justify-center lg:justify-start gap-1 lg:gap-1.5 transition-all ${
                  activeTab === tab.id
                    ? 'bg-slate-900 text-white shadow-md'
                    : isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>{tab.icon}</span>
                <span className="truncate">{tab.label || t(tab.labelKey)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Map Section — only renders when Markers tab active. Explicit inline height so we
            don't depend on Tailwind generating arbitrary value classes (h-[calc(...)]) or on
            a fragile flex chain. Inline styles always win. */}
        <div
          className={`${activeTab === 'markers' ? 'block' : 'hidden'} ${mapFullscreen ? 'fixed inset-0 z-[2000]' : 'relative z-0'} w-full`}
          style={mapFullscreen
            ? { height: '100vh', width: '100vw' }
            : window.matchMedia('(min-width: 1024px)').matches
              ? { height: 'calc(100vh - 120px)', minHeight: '400px' }
              : { height: 'calc(100vh - 170px)', minHeight: '400px' }
          }
        >
          {/* Floating Map Header Pill (top-left).
              Mobile: compact "🗺️ Map · N" (small label + count). Desktop: full pill with "Travel Tools" subtitle (unchanged). */}
          <div className={`absolute top-3 left-3 lg:top-4 lg:left-4 z-[1050] rounded-2xl shadow-lg px-2.5 lg:px-4 py-1.5 lg:py-2.5 flex items-center gap-1.5 lg:gap-3 backdrop-blur ${isDarkMode ? 'bg-gray-800/95 text-white' : 'bg-white/95 text-gray-900'}`}>
            <span className="text-sm lg:text-lg">🗺️</span>
            <div className="min-w-0 hidden lg:block">
              <div className="text-[10px] uppercase tracking-wider opacity-60 font-bold leading-none">{t('my_morocco')}</div>
              <div className="text-sm font-extrabold truncate">{t('morocco_map_label')} · {markers.length} {markers.length === 1 ? t('morocco_marker_count_one') : t('morocco_marker_count_many')}</div>
            </div>
            <div className="lg:hidden text-xs font-extrabold">{t('morocco_map_label')} · {markers.length}</div>
            <button
              onClick={() => setIsAddingMarker(!isAddingMarker)}
              className={`shrink-0 ml-1 px-2 lg:px-3 py-1 lg:py-1.5 rounded-lg text-[11px] lg:text-xs font-bold transition-colors ${isAddingMarker ? 'bg-red-500 text-white' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
            >
              {isAddingMarker ? '✕' : t('morocco_add_marker_button')}
            </button>
          </div>

          {/* Add-Marker toolkit — visible only while in add-marker mode AND modal not yet open.
              Sits right below the Map header pill. Tells the user what to do, plus an alternative:
              pick a city from the dropdown to drop the marker at that city's coordinates. */}
          {isAddingMarker && !showEmojiPicker && (
            <div className={`absolute top-12 lg:top-20 left-3 lg:left-4 z-[1050] rounded-lg lg:rounded-xl shadow lg:shadow-lg px-2 py-1.5 lg:px-3 lg:py-2 max-w-[200px] sm:max-w-[260px] lg:max-w-md backdrop-blur ring-1 ring-amber-400/40 ${isDarkMode ? 'bg-amber-900/90 text-amber-50' : 'bg-amber-50/95 text-amber-900'}`}>
              <div className="flex items-start gap-1.5 lg:gap-2">
                <span className="text-sm lg:text-xl shrink-0 leading-none mt-0.5">📍</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] leading-tight lg:text-sm font-bold">
                    <span className="lg:hidden">{t('morocco_add_note_hint_short')}</span>
                    <span className="hidden lg:inline">{t('morocco_add_note_hint_long')}</span>
                  </div>
                  <div className="hidden lg:block text-[10px] lg:text-xs opacity-80 mt-0.5">{t('morocco_or_pick_city_below')}</div>
                  <select
                    value=""
                    onChange={(e) => {
                      const city = cities.find(c => c.id === e.target.value);
                      if (city) handleMapClick({ lat: city.lat, lng: city.lng });
                    }}
                    className={`mt-1 lg:mt-1.5 w-full text-[10px] lg:text-xs px-1.5 py-0.5 lg:px-2 lg:py-1 rounded border ${isDarkMode ? 'bg-gray-800 border-amber-700 text-white' : 'bg-white border-amber-400 text-gray-800'}`}
                  >
                    <option value="">{t('morocco_pick_a_city')}</option>
                    {[...cities].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => (
                      <option key={c.id} value={c.id}>{c.name}{c.region ? ` · ${c.region}` : ''}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
          {/* Fullscreen toggle (mobile only).
              Not fullscreen: bottom-28 (well clear of the h-14 sticky bottom nav — was too close at bottom-20).
              Fullscreen: top-3 right-3 (easy thumb reach at top corner, can't get hidden by any browser chrome). */}
          <button
            onClick={() => setMapFullscreen(!mapFullscreen)}
            className={`lg:hidden ${mapFullscreen ? 'fixed top-3 right-3' : 'absolute bottom-28 right-3'} z-[1100] w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-xl border-2 transition-transform active:scale-95 ${mapFullscreen ? 'bg-red-500 text-white border-red-600' : isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
            aria-label={mapFullscreen ? t('morocco_exit_fullscreen') : t('morocco_fullscreen_map')}
          >
            {mapFullscreen ? '✕' : '⤢'}
          </button>
          {/* ⚠️ Disclaimer icon — DESKTOP only. Replaces the long yellow bar that lived below
              the page (looked bad). Bottom-right corner of the map — empty space on desktop
              (the mobile fullscreen toggle that lives there is lg:hidden, so no conflict).
              Avoid bottom-left because the route info card already sits there at 340px wide. */}
          <button
            onClick={() => setShowDisclaimerModal(true)}
            className="hidden lg:flex absolute bottom-8 right-3 z-[1100] w-9 h-9 rounded-full items-center justify-center text-lg shadow-lg border-2 bg-yellow-50 hover:bg-yellow-100 border-yellow-400 text-yellow-700 transition-colors"
            aria-label={t('morocco_privacy_disclaimer')}
            title={t('morocco_privacy_disclaimer')}
          >
            ⚠️
          </button>
          <MapContainer
            center={[31.5, -7.5]}
            zoom={6}
            minZoom={5}
            maxZoom={12}
            maxBounds={[[20.0, -18], [37.0, 2]]}
            maxBoundsViscosity={1.0}
            style={{ height: '100%', width: '100%' }}
            className={isAddingMarker ? 'cursor-crosshair' : ''}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            
            <MapResizeHandler activeTab={activeTab} fullscreen={mapFullscreen} />
            <MapClickHandler onMapClick={handleMapClick} isAddingMarker={isAddingMarker} />
            
            {/* City markers with names — click shows the city-name popup only. Prayer times are
                already available via the Prayer widget in the right drawer; no duplicate UI here. */}
            {cities.map(city => (
              <Marker
                key={city.id}
                position={[city.lat, city.lng]}
                icon={L.divIcon({
                  html: `<div class="city-marker-wrapper">
                    <div class="city-dot-new"></div>
                    <div class="city-name-label">${city.name}</div>
                  </div>`,
                  className: 'city-marker-container',
                  iconSize: [80, 30],
                  iconAnchor: [40, 15]
                })}
              >
                <Popup>
                  <div className="text-center">
                    <strong>{city.name}</strong>
                    {city.name_ar && <div className="text-gray-500">{city.name_ar}</div>}
                  </div>
                </Popup>
              </Marker>
            ))}
            
            {/* User markers */}
            {markers.map(marker => {
              const popupImgUrl = findEmojiImageUrl(marker.emoji);
              return (
              <Marker
                key={marker.id}
                position={[marker.lat, marker.lng]}
                icon={createEmojiIcon(displayEmojiChar(marker.emoji), popupImgUrl)}
                eventHandlers={{
                  click: () => {
                    setSelectedMarker(marker);
                    setActiveTab('markers');
                  }
                }}
              >
                <Popup className="tt-marker-popup">
                  <div className="tt-marker-card">
                    <div className="tt-marker-card-head">
                      {popupImgUrl ? (
                        <img src={popupImgUrl} alt="" className="tt-marker-card-emoji" style={{ width: '1.5em', height: '1.5em', borderRadius: '50%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <span className="tt-marker-card-emoji">{displayEmojiChar(marker.emoji)}</span>
                      )}
                      <div className="tt-marker-card-title">{marker.title}</div>
                    </div>
                    {marker.content && (
                      <p className="tt-marker-card-body">{marker.content}</p>
                    )}
                    {marker.city_id && (
                      <div className="tt-marker-card-city">
                        <span>📍</span>
                        <span>{getCityName(marker.city_id)}</span>
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
              );
            })}

            {/* New marker preview */}
            {selectedPosition && (
              <Marker
                position={[selectedPosition.lat, selectedPosition.lng]}
                icon={createEmojiIcon(displayEmojiChar(newMarker.emoji), findEmojiImageUrl(newMarker.emoji))}
              />
            )}

            {/* 📏 Real route line from OSRM — follows actual roads. Fallback to straight line if API failed. */}
            {routeData && routeData.coordinates && routeData.coordinates.length > 1 && (
              <Polyline
                positions={routeData.coordinates}
                pathOptions={{
                  color: '#3B82F6',
                  weight: 5,
                  opacity: 0.85,
                  // Solid line for real OSRM route, dashed only for fallback straight line
                  dashArray: routeData.source === 'fallback' ? '10 6' : undefined,
                  lineCap: 'round',
                  lineJoin: 'round',
                }}
              />
            )}
          </MapContainer>

          {/* Prayer-on-map-click popup removed — duplicate of the Prayer widget in the right drawer.
              Per user feedback: clicking a city should just show the city's name popup (above), nothing else. */}

          {/* 📏 Distance toggle. Mobile: icon-only round button. Desktop: full pill with label (unchanged).
              In MOBILE fullscreen mode, pushed DOWN to top-20 so the fullscreen-exit ✕ button at top-3 doesn't overlap it. */}
          <button
            onClick={() => setDistancePanelOpen(v => !v)}
            className={`absolute ${mapFullscreen ? 'top-20' : 'top-4'} right-3 lg:top-4 lg:right-4 z-[1050] h-10 w-10 lg:w-auto lg:px-3 rounded-full shadow-lg flex items-center justify-center lg:gap-1.5 text-sm font-bold transition-all hover:scale-105 ${
              distancePanelOpen
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                : isDarkMode ? 'bg-gray-800 text-white border border-gray-600' : 'bg-white text-gray-800 border border-gray-200'
            }`}
            title={t('morocco_compare_distance')}
            aria-label={t('morocco_distance_button')}
          >
            <span className="text-base">📏</span>
            <span className="hidden lg:inline">{t('morocco_distance_button')}</span>
          </button>

          {/* 📋 Markers list toggle. Mobile: small round badge with count. Desktop: full pill (unchanged).
              In MOBILE fullscreen mode, pushed DOWN to top-32 so it stacks under the moved Distance button. */}
          <button
            onClick={() => setMarkersPanelOpen(v => !v)}
            className={`absolute ${mapFullscreen ? 'top-32' : 'top-16'} right-3 lg:top-16 lg:right-4 z-[1050] h-10 w-10 lg:w-auto lg:px-3 rounded-full shadow-lg flex items-center justify-center lg:gap-1.5 text-xs lg:text-sm font-bold transition-all hover:scale-105 ${
              markersPanelOpen
                ? 'bg-slate-900 text-white'
                : isDarkMode ? 'bg-gray-800 text-white border border-gray-600' : 'bg-white text-gray-800 border border-gray-200'
            }`}
            title={`${t('morocco_tab_markers')} (${markers.length})`}
            aria-label={`${t('morocco_tab_markers')}, ${markers.length}`}
          >
            <span className="text-base lg:hidden">📋</span>
            <span className="hidden lg:inline text-base">📋</span>
            <span className="hidden lg:inline">{t('morocco_tab_markers')} · {markers.length}</span>
            {/* Mobile-only count badge in the corner of the round button */}
            <span className="lg:hidden absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-extrabold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow">{markers.length}</span>
          </button>

          {/* Distance picker panel.
              Mobile: full-width pill at top of map (below the buttons). Route card lives at bottom — no overlap.
              Desktop: floating draggable card top-right (unchanged). */}
          {distancePanelOpen && (
            <div
              className={`absolute z-[1050] rounded-2xl shadow-xl p-2.5
                left-3 right-3 top-28 max-w-xs mx-auto
                lg:left-auto lg:right-4 lg:top-16 lg:mx-0 lg:max-w-none lg:w-72 lg:p-4
                ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
              // Only apply transform when actually dragged. translate(0,0) creates a stacking context
              // that breaks iOS Safari's native <select> dropdowns inside this container — exactly the
              // "tap TO and nothing happens" symptom the user reports.
              style={(distPanelOffset.x !== 0 || distPanelOffset.y !== 0)
                ? { transform: `translate(${distPanelOffset.x}px, ${distPanelOffset.y}px)` }
                : undefined}>
              <div
                onMouseDown={(e) => startDrag('panel', distPanelOffset, e)}
                onTouchStart={(e) => startDrag('panel', distPanelOffset, e)}
                className="flex items-center justify-between mb-3 cursor-move select-none"
                title={t('morocco_drag_to_move')}
              >
                <div>
                  <div className="text-[10px] uppercase tracking-wider opacity-60 font-bold flex items-center gap-1">
                    <span className="opacity-50">⋮⋮</span> {t('morocco_trip_planner')}
                  </div>
                  <div className="text-sm font-extrabold">{stops.length} stops</div>
                </div>
                {/* ✕ on picker just hides the picker — keeps the cities + route line on the map.
                    To fully clear, the user empties the stop dropdowns or hits Reset on the route card. */}
                <button onClick={() => { setDistancePanelOpen(false); }}
                  className={`w-7 h-7 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>✕</button>
              </div>
              {/* Numbered stop list — A (green) start, last (red) end, middle (blue) via stops.
                  Dashed vertical connector implies "you'll travel along here". */}
              <div className="space-y-1">
                {stops.map((value, idx) => {
                  const isFirst = idx === 0;
                  const isLast  = idx === stops.length - 1;
                  const badgeCls = isFirst ? 'bg-emerald-500' : isLast ? 'bg-rose-500' : 'bg-blue-500';
                  const badgeLabel = String.fromCharCode(65 + idx); // A, B, C, …
                  const labelText  = isFirst ? t('morocco_trip_start') : isLast ? t('morocco_trip_end') : `${t('morocco_trip_via')} ${idx}`;
                  const placeholder = isFirst ? t('morocco_trip_pick_start_city') : isLast ? t('morocco_trip_pick_end_city') : t('morocco_trip_pick_a_stop');
                  return (
                    <div key={idx}>
                      <div className="flex items-center gap-2">
                        <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-extrabold shadow ${badgeCls}`}>
                          {badgeLabel}
                        </span>
                        <div className="flex-1 min-w-0">
                          <label className="text-[9px] uppercase tracking-wider opacity-60 font-bold">{labelText}</label>
                          <select
                            value={value}
                            onChange={(e) => updateStop(idx, e.target.value)}
                            className={`w-full mt-0.5 px-2 py-1.5 rounded-md text-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
                          >
                            <option value="">{placeholder}</option>
                            {cities.map(c => (
                              <option key={c.id} value={c.name}>
                                {c.name}{c.name_ar ? ` (${c.name_ar})` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        {/* ✕ — only when we have more than 2 stops (can't go below the min) */}
                        {stops.length > 2 && (
                          <button
                            onClick={() => removeStop(idx)}
                            className="shrink-0 w-7 h-7 rounded-md bg-red-900/40 hover:bg-red-800/70 text-red-200 text-xs"
                            title={t('morocco_remove_stop')}
                            aria-label={t('morocco_remove_stop')}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      {/* Vertical dashed connector between rows */}
                      {!isLast && <div className="ml-3.5 my-1 h-3 border-l-2 border-dashed border-gray-500/50"></div>}
                    </div>
                  );
                })}
                {/* + Add location — secondary action, dashed border. */}
                <button
                  onClick={addStop}
                  disabled={stops.length >= MAX_STOPS}
                  className={`w-full mt-2 py-2 rounded-lg border-2 border-dashed text-sm font-bold transition-colors
                    ${stops.length >= MAX_STOPS
                      ? 'border-gray-500/40 text-gray-500 cursor-not-allowed opacity-50'
                      : 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10'}`}
                >
                  {stops.length >= MAX_STOPS ? `${t('morocco_max_stops')} ${MAX_STOPS}` : t('morocco_add_location')}
                </button>
                {/* GO — primary action. Routing happens ONLY on this click, not when stops change.
                    Disabled until ≥2 stops are filled, no consecutive duplicates, and cities are loaded. */}
                {(() => {
                  const filled = stops.filter(Boolean);
                  const hasDup = filled.some((s, i) => i > 0 && s === filled[i - 1]);
                  const canGo  = filled.length >= 2 && !hasDup && cities.length > 0 && !routeLoading;
                  return (
                    <button
                      onClick={computeRoute}
                      disabled={!canGo}
                      className={`w-full mt-2 py-2.5 rounded-lg text-sm font-extrabold transition-colors shadow
                        ${canGo
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                          : 'bg-gray-500/30 text-gray-400 cursor-not-allowed'}`}
                      title={canGo ? t('morocco_compute_route_now') : t('morocco_fill_at_least_2')}
                    >
                      {routeLoading ? t('morocco_trip_routing') : `${t('morocco_trip_go_button')} · ${filled.length} ${t('morocco_trip_stops_suffix')}`}
                    </button>
                  );
                })()}
                {/* RESET — clears stops back to 2 empties + removes the drawn route from the map.
                    Stays in the picker (panel doesn't close) so the user can start a fresh trip. */}
                <button
                  onClick={() => {
                    setStops(['', '']);
                    setRouteData(null);
                    setRouteCardOffset({ x: 0, y: 0 });
                  }}
                  className={`w-full mt-2 py-1.5 rounded-lg text-xs font-bold transition-colors ${isDarkMode ? 'text-gray-400 hover:text-red-300 hover:bg-red-900/20' : 'text-gray-500 hover:text-red-600 hover:bg-red-50'}`}
                  title={t('morocco_clear_all_stops')}
                >
                  {t('morocco_trip_reset')}
                </button>
              </div>
              <div className={`mt-3 pt-3 border-t text-[10px] opacity-60 text-center ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                {routeData
                  ? t('morocco_trip_hint_after')
                  : t('morocco_trip_hint_initial')}
              </div>
            </div>
          )}

          {/* 📋 Markers list panel.
              Mobile: FULL-screen overlay portaled to document.body so the page's Travel Tools header
                      (z-30) can't peek through. Header shows "MARKERS" with X (close) on the right.
                      X closes the panel, returning the user to the map.
              Desktop: floating panel top-right inside the map (unchanged). */}
          {markersPanelOpen && (() => {
            const isMobile = !window.matchMedia('(min-width: 1024px)').matches;
            const panelJSX = (
            <>
              {/* Mobile-only backdrop — tap to close. z above page header (z-30) AND above the
                  fullscreen map container (z-2000) so the modal works when map is fullscreen. */}
              <div
                onClick={() => setMarkersPanelOpen(false)}
                className="lg:hidden fixed inset-0 z-[2100] bg-black/40 backdrop-blur-sm"
                aria-label={t('morocco_close_markers_list')}
              />
              <div className={`shadow-2xl overflow-hidden flex flex-col
                fixed inset-0 z-[2150] rounded-none
                lg:absolute lg:inset-x-auto lg:inset-y-auto lg:top-28 lg:right-4 lg:bottom-auto lg:w-80 lg:max-h-[calc(100vh-260px)] lg:z-[1050] lg:rounded-2xl
                ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              {/* Panel header — single line: "MARKERS · N markers" on left + ✕ close on right */}
              <div className={`px-3 lg:px-4 py-2.5 flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-900 text-white shrink-0`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-base">📋</span>
                  <div className="min-w-0">
                    <div className="text-[9px] uppercase tracking-wider opacity-80 font-bold leading-none">{t('morocco_tab_markers')}</div>
                    <div className="text-sm font-extrabold truncate">{markers.length} {markers.length === 1 ? t('morocco_marker_count_one') : t('morocco_marker_count_many')}</div>
                  </div>
                </div>
                {/* ✕ close button — closes the modal and returns to the map (no router navigation) */}
                <button
                  onClick={() => setMarkersPanelOpen(false)}
                  className="w-8 h-8 lg:w-6 lg:h-6 rounded-md bg-white/20 hover:bg-white/30 text-base lg:text-xs shrink-0 flex items-center justify-center"
                  aria-label={t('close')}
                  title={t('close')}
                >
                  ✕
                </button>
              </div>
              {/* Scrollable body: list of markers (with inline edit) + add-by-city dropdown */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {markers.length === 0 ? (
                  <div className={`text-center py-6 px-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('morocco_no_markers_yet')}
                  </div>
                ) : (
                  markers.map(marker => {
                    const isSelected = selectedMarker?.id === marker.id;
                    const isEditing = editingMarker?.id === marker.id;
                    const imgUrl = findEmojiImageUrl(marker.emoji);
                    return (
                      <div
                        key={marker.id}
                        className={`p-2.5 rounded-lg border transition-colors ${
                          isSelected
                            ? (isDarkMode ? 'bg-blue-900/40 border-blue-500' : 'bg-blue-50 border-blue-400')
                            : (isDarkMode ? 'bg-gray-700/60 border-gray-700' : 'bg-gray-50 border-gray-100')
                        }`}
                      >
                        {isEditing ? (
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={editingMarker.emoji || ''}
                                onChange={(e) => setEditingMarker({ ...editingMarker, emoji: e.target.value })}
                                className={`w-14 px-2 py-1 rounded text-xl text-center ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                              />
                              <input
                                type="text"
                                value={editingMarker.title || ''}
                                onChange={(e) => setEditingMarker({ ...editingMarker, title: e.target.value })}
                                placeholder={t('morocco_marker_title_placeholder')}
                                className={`flex-1 min-w-0 px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                              />
                            </div>
                            <textarea
                              value={editingMarker.content || ''}
                              onChange={(e) => setEditingMarker({ ...editingMarker, content: e.target.value })}
                              rows={2}
                              placeholder={t('morocco_marker_notes_placeholder')}
                              className={`w-full px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                            />
                            <div className="flex gap-2">
                              <button onClick={() => updateMarker(marker.id)} className="flex-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-bold">💾 {t('save')}</button>
                              <button onClick={() => setEditingMarker(null)} className={`px-3 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-800'}`}>{t('cancel')}</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2 min-w-0 flex-1">
                              {imgUrl ? (
                                <img src={imgUrl} alt={marker.title || ''} className="w-8 h-8 rounded-full object-cover shrink-0 mt-0.5 border border-white shadow" onError={(e) => { e.target.style.display = 'none'; }} />
                              ) : (
                                <span className="text-2xl shrink-0 leading-none">{displayEmojiChar(marker.emoji)}</span>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className={`font-bold text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{marker.title}</div>
                                {marker.content && (
                                  <p className={`text-[11px] leading-snug mt-0.5 line-clamp-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{marker.content}</p>
                                )}
                                {marker.city_id && (
                                  <span className={`inline-block text-[10px] font-bold mt-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>📍 {getCityName(marker.city_id)}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col gap-1 shrink-0">
                              <button onClick={() => setEditingMarker(marker)} className={`p-1 rounded text-sm ${isDarkMode ? 'hover:bg-blue-900/50 text-blue-400' : 'hover:bg-blue-100 text-blue-500'}`} title={t('morocco_edit')}>✏️</button>
                              <button onClick={() => deleteMarker(marker.id)} className={`p-1 rounded text-sm ${isDarkMode ? 'hover:bg-red-900/50 text-red-400' : 'hover:bg-red-100 text-red-500'}`} title={t('morocco_delete')}>🗑️</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                {/* Add via city dropdown — fallback when emoji markers cover city dots */}
                <div className={`mt-2 pt-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className={`text-[10px] uppercase tracking-wider font-bold mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('morocco_add_marker_at_city')}</div>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        const city = cities.find(c => c.id === e.target.value);
                        if (city) {
                          setSelectedPosition({ lat: city.lat, lng: city.lng });
                          setShowEmojiPicker(true);
                          setNewMarker({ ...newMarker, city_id: city.id });
                        }
                      }
                    }}
                    className={`w-full px-3 py-2 rounded-lg text-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                  >
                    <option value="">{t('morocco_select_a_city')}</option>
                    {cities.map(city => (
                      <option key={city.id} value={city.id}>{city.name}{city.name_ar ? ` (${city.name_ar})` : ''}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            </>
            );
            // On mobile, portal to document.body so the modal escapes the map div's stacking context
            // (relative z-0). Without the portal, the page header at z-30 sits ABOVE this modal's z-1950.
            return isMobile ? createPortal(panelJSX, document.body) : panelJSX;
          })()}

          {/* 📱 MOBILE route panel — single collapsible card under zoom.
              Collapsed: one round icon showing the selected mode emoji.
              Expanded:  small card with distance, selected mode info, switcher chips, reset.
              Tap the card header to collapse; tap the icon to expand. */}
          {routeData && routeData.stops && routeData.stops.length >= 2 && (() => {
            const tripStops = routeData.stops;
            const km = routeData.km;
            const carMin = routeData.durationMin;
            const busMin = Math.round(carMin * 1.5);
            const trainMin = Math.round(carMin * 0.9);
            const walkMin = Math.round(km / 5 * 60);
            const walkLong = km > 50;
            // Train available only when EVERY stop is on the ONCF rail network.
            const hasRail = tripStops.every(s => ONCF_RAIL_CITIES.has(s));
            // Compact header label: with ≥4 stops, collapse middle to "+N more" so it fits on phones.
            const stopsLabel = tripStops.length > 3
              ? `${tripStops[0]} → ${tripStops[tripStops.length - 1]} · +${tripStops.length - 2} more`
              : tripStops.join(' → ');
            // Prices intentionally omitted — bus/train fares change frequently and would mislead users.
            // Walking shows "Free" since it's not a price the user could be wrong about.
            const modes = [
              { id: 'car',   icon: '🚗', label: 'Car',   min: carMin,   available: true },
              { id: 'bus',   icon: '🚌', label: 'Bus',   min: busMin,   available: true },
              { id: 'train', icon: '🚆', label: 'Train', min: trainMin, available: hasRail },
              { id: 'walk',  icon: '🚶', label: 'Walk',  min: walkMin,  note: walkLong ? t('morocco_too_far') : t('morocco_free'), available: !walkLong },
            ];
            const current = modes.find(m => m.id === selectedTransportMode) || modes[0];
            // Mobile ✕ — must explicitly clear routeData too, otherwise the polyline + card stay
            // (we no longer auto-clear routeData on stops change since routing is Go-button only).
            const clearRoute = () => {
              setStops(['', '']);
              setRouteData(null);
              setDistancePanelOpen(false);
              setRouteCardOffset({ x: 0, y: 0 });
              setDistPanelOffset({ x: 0, y: 0 });
            };

            if (!routeInfoExpanded) {
              // COLLAPSED — just the current-mode icon + a small reset button
              return (
                <div className="lg:hidden absolute left-3 z-[1050] flex flex-col gap-1.5" style={{ top: '128px' }}>
                  <button
                    onClick={() => setRouteInfoExpanded(true)}
                    className={`w-11 h-11 rounded-full shadow-lg flex items-center justify-center text-xl transition-transform active:scale-95 ${isDarkMode ? 'bg-slate-900 text-white' : 'bg-slate-900 text-white'}`}
                    title={`${current.label} · ${_fmtDuration(current.min)} — tap for details`}
                    aria-label={t('morocco_show_route_details')}
                  >
                    {current.icon}
                  </button>
                  <button
                    onClick={clearRoute}
                    className={`w-11 h-11 rounded-full shadow-md flex items-center justify-center text-sm transition-colors ${isDarkMode ? 'bg-red-900 text-red-200 hover:bg-red-800' : 'bg-white text-red-500 hover:bg-red-50'}`}
                    title={t('morocco_clear_route')}
                    aria-label={t('morocco_clear_route')}
                  >
                    ✕
                  </button>
                </div>
              );
            }

            // EXPANDED — small card with distance, selected mode, mode chips, reset
            return (
              <div
                className={`lg:hidden absolute left-3 z-[1050] w-56 rounded-xl shadow-xl overflow-hidden ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
                style={{ top: '128px' }}
              >
                {/* Header — tap to collapse */}
                <button
                  onClick={() => setRouteInfoExpanded(false)}
                  className={`w-full px-3 py-2 flex items-center justify-between text-left ${isDarkMode ? 'bg-slate-900 text-white' : 'bg-slate-900 text-white'}`}
                  title={t('morocco_collapse')}
                >
                  <div className="min-w-0">
                    <div className="text-[9px] uppercase tracking-wider opacity-70 font-bold leading-none truncate">{stopsLabel}</div>
                    <div className="text-xs font-extrabold mt-0.5">{km} km · {Math.round(km * 0.621371)} mi</div>
                  </div>
                  <span className="text-xs opacity-70 ml-2">▾</span>
                </button>
                {/* Selected mode summary */}
                <div className={`px-3 py-2 ${isDarkMode ? 'bg-gray-700/40' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{current.icon}</span>
                    <div className="min-w-0">
                      <div className={`text-[10px] uppercase tracking-wider font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{current.label}</div>
                      <div className="text-sm font-extrabold">{current.available ? `${_fmtDuration(current.min)}` : '—'}</div>
                      {current.id === 'walk' && current.note ? (
                        <div className={`text-[11px] font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{current.note}</div>
                      ) : !current.available ? (
                        <div className={`text-[11px] font-bold ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Not available</div>
                      ) : null}
                    </div>
                  </div>
                </div>
                {/* Mode switcher — small horizontal chips */}
                <div className="px-2 py-2 flex items-center gap-1">
                  {modes.map(m => {
                    const isSelected = m.id === selectedTransportMode;
                    return (
                      <button
                        key={m.id}
                        onClick={() => setSelectedTransportMode(m.id)}
                        disabled={!m.available}
                        className={`flex-1 h-9 rounded-md text-base transition-all flex items-center justify-center
                          ${isSelected
                            ? 'bg-blue-500 text-white shadow scale-105'
                            : (isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white text-gray-800 hover:bg-gray-100 border border-gray-200')}
                          ${!m.available ? 'opacity-40' : ''}`}
                        title={`${m.label} · ${_fmtDuration(m.min)}`}
                      >
                        {m.icon}
                      </button>
                    );
                  })}
                  <button
                    onClick={clearRoute}
                    className={`h-9 px-2 rounded-md text-xs font-bold transition-colors ${isDarkMode ? 'bg-red-900 text-red-200 hover:bg-red-800' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                    title={t('morocco_clear_route')}
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Route info card — detailed: distance + driving time + per-mode times + cost estimates + travel tip.
              Gated by !routeCardHidden so the user can dismiss the card while keeping the route line on the map.
              DESKTOP-ONLY now (mobile uses the vertical mode stack above). */}
          {routeData && routeData.stops && routeData.stops.length >= 2 && !routeCardHidden && (() => {
            const tripStops = routeData.stops;
            const hasRail = tripStops.every(s => ONCF_RAIL_CITIES.has(s));
            // Desktop header — full join, will truncate via CSS when long.
            const stopsLabelFull = tripStops.join(' → ');
            if (routeLoading && !routeData) {
              return (
                <div className={`absolute left-3 bottom-3 z-[1050] w-64 rounded-xl shadow-xl px-3 py-2.5 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
                  <div className="text-xs font-bold">📏 Fetching real route…</div>
                  <div className="text-[10px] opacity-60 mt-0.5">{stopsLabelFull}</div>
                </div>
              );
            }
            if (!routeData) return null;
            const km = routeData.km;
            const carMin = routeData.durationMin;
            const busMin = Math.round(carMin * 1.5);
            const trainMin = Math.round(carMin * 0.9);
            const walkMin = Math.round(km / 5 * 60);
            const walkLong = km > 50;
            // Prices intentionally omitted — bus/train fares and fuel costs change frequently;
            // showing stale numbers would mislead users. Only duration is shown.
            // Travel tip based on distance
            let tip;
            if (km < 50) tip = 'Easy day trip — back same day';
            else if (km < 200) tip = 'Day trip doable · early start recommended';
            else if (km < 500) tip = 'Long day or overnight · consider train for comfort';
            else tip = 'Better as multi-day · train or overnight bus';
            return (
              <div className={`absolute z-[1050] rounded-xl shadow-2xl overflow-hidden
                hidden lg:block
                left-3 right-3 bottom-3 max-w-sm mx-auto
                lg:left-3 lg:right-auto lg:bottom-3 lg:mx-0 lg:max-w-none lg:w-[340px]
                ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
                style={{ transform: `translate(${routeCardOffset.x}px, ${routeCardOffset.y}px)` }}>
                {/* Header — drag handle */}
                <div
                  onMouseDown={(e) => startDrag('route', routeCardOffset, e)}
                  onTouchStart={(e) => startDrag('route', routeCardOffset, e)}
                  className="px-3 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white flex items-center justify-between gap-2 cursor-move select-none"
                  title={t('morocco_drag_to_move')}
                >
                  <div className="min-w-0 flex items-center gap-2">
                    <span className="opacity-60 text-xs">⋮⋮</span>
                    <div className="min-w-0">
                      <div className="text-[9px] uppercase tracking-wider opacity-90 font-bold leading-none">
                        Route {routeData.source === 'osrm' ? '· live' : '· est.'}
                      </div>
                      {/* Stops list — wraps to up to 2 lines so 4+ stop trips show all cities
                          instead of getting cut off with an ellipsis (e.g. "Tangier → Al Hoceima → Rabat → Ou..."). */}
                      <div className={`font-extrabold leading-snug break-words ${tripStops.length > 3 ? 'text-[11px]' : 'text-sm'}`}
                           style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {stopsLabelFull}
                      </div>
                    </div>
                  </div>
                  {/* ✕ on route card hides the CARD but keeps the polyline on the map.
                      User can re-open the picker to change cities (which clears the line via
                      "Select departure city..." or by picking new ones). */}
                  <button onClick={(e) => { e.stopPropagation(); setRouteCardHidden(true); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    className="shrink-0 w-6 h-6 rounded-md bg-white/20 hover:bg-white/30 text-xs">✕</button>
                </div>
                {/* Hero stats — distance (km + mi) and driving time front and center */}
                <div className={`px-4 py-3 ${isDarkMode ? 'bg-gray-700/40' : 'bg-gray-50'}`}>
                  <div className="flex items-baseline justify-between">
                    <div>
                      <div className={`text-2xl font-extrabold leading-none ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {km} <span className="text-xs font-bold opacity-60">km</span>
                        <span className={`text-xs font-bold ml-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>· {Math.round(km * 0.621371)} mi</span>
                      </div>
                      <div className={`text-[10px] uppercase tracking-wider font-bold mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{routeData.source === 'osrm' ? 'By road' : 'Estimated'}</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-extrabold leading-none ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{_fmtDuration(carMin)}</div>
                      <div className={`text-[10px] uppercase tracking-wider font-bold mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Driving</div>
                    </div>
                  </div>
                </div>
                {/* Transport breakdown — per-mode row with time + cost + brief tag */}
                <div className="p-3 space-y-1.5">
                  {/* Car */}
                  <div className={`flex items-center gap-3 px-2 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700/40' : 'bg-gray-50'}`}>
                    <span className="text-xl shrink-0">🚗</span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Car</div>
                      <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Driving</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-xs font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{_fmtDuration(carMin)}</div>
                    </div>
                  </div>
                  {/* Bus */}
                  <div className={`flex items-center gap-3 px-2 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700/40' : 'bg-gray-50'}`}>
                    <span className="text-xl shrink-0">🚌</span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>CTM / Supratours</div>
                      <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Long-distance bus</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-xs font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{_fmtDuration(busMin)}</div>
                    </div>
                  </div>
                  {/* Train */}
                  <div className={`flex items-center gap-3 px-2 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700/40' : 'bg-gray-50'} ${hasRail ? '' : 'opacity-50'}`}>
                    <span className="text-xl shrink-0">🚆</span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>ONCF Train</div>
                      <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{hasRail ? 'AC · comfortable · 2nd class' : 'No direct line to this city'}</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-xs font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{hasRail ? _fmtDuration(trainMin) : '—'}</div>
                    </div>
                  </div>
                  {/* Walking */}
                  <div className={`flex items-center gap-3 px-2 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700/40' : 'bg-gray-50'} ${walkLong ? 'opacity-50' : ''}`}>
                    <span className="text-xl shrink-0">🚶</span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Walking</div>
                      <div className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{walkLong ? 'Not realistic over 50 km' : 'On foot'}</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-xs font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{walkLong ? `~${Math.round(km / 5 / 8)} days` : _fmtDuration(walkMin)}</div>
                    </div>
                  </div>
                </div>
                {/* Per-leg breakdown — only shown for 3+ stop trips (≥2 legs). One liner per leg. */}
                {routeData.legs && routeData.legs.length > 1 && (
                  <div className={`px-3 pb-2 pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Legs</div>
                    <ol className="space-y-0.5">
                      {routeData.legs.map((leg, i) => (
                        <li key={i} className="flex items-center justify-between text-[11px]">
                          <span className={`truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            <span className="font-bold">{i + 1}.</span> {leg.from} → {leg.to}
                          </span>
                          <span className={`shrink-0 ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {leg.km} km · {_fmtDuration(leg.min)}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {/* Travel tip — distance-based suggestion */}
                <div className={`px-3 py-2 border-t flex items-start gap-2 ${isDarkMode ? 'bg-gray-900/40 border-gray-700 text-gray-300' : 'bg-amber-50 border-gray-100 text-amber-800'}`}>
                  <span className="text-sm shrink-0 leading-none mt-0.5">💡</span>
                  <span className="text-[11px] font-bold leading-snug">{tip}</span>
                </div>
                {/* Disclaimer */}
                <div className={`px-3 py-1.5 text-[9px] text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Estimates only · actual costs and times vary
                </div>
              </div>
            );
          })()}
          
          {/* Emoji picker overlay — modal-style, viewport-anchored so it never clips.
              z-[3000] sits above the fullscreen map container (z-[2000]) so the picker shows
              in BOTH normal and fullscreen map modes. */}
          {showEmojiPicker && selectedPosition && (
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-3 sm:p-4"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)' }}
              onClick={(e) => {
                // Click outside content closes the modal
                if (e.target === e.currentTarget) {
                  setShowEmojiPicker(false);
                  setSelectedPosition(null);
                }
              }}
            >
              <div className={`w-full max-w-md max-h-[80vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden ring-1 ${isDarkMode ? 'bg-gray-900 ring-gray-700' : 'bg-white ring-gray-200'}`}>
                {/* ── Header ─────────────────────────────────────── */}
                <div className={`flex items-center justify-between gap-3 px-4 py-3 ${isDarkMode ? 'bg-gradient-to-r from-blue-900/50 to-purple-900/40 border-b border-gray-700' : 'bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-100'}`}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xl shrink-0 shadow-sm overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                      {(() => {
                        const previewImg = findEmojiImageUrl(newMarker.emoji);
                        if (previewImg) return <img src={previewImg} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />;
                        return displayEmojiChar(newMarker.emoji);
                      })()}
                    </div>
                    <div className="min-w-0">
                      <div className={`text-[15px] font-bold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('morocco_add_note_title')}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowEmojiPicker(false); setSelectedPosition(null); }}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-white/80 hover:bg-white text-gray-500 hover:text-gray-700'}`}
                    aria-label={t('close')}
                  >
                    ✕
                  </button>
                </div>

                {/* ── Scrollable body ───────────────────────────── */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                  {/* Icon picker */}
                  <div>
                    <label className={`block text-[11px] font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {t('morocco_note_icon_label')}
                    </label>
                    <div className={`grid grid-cols-6 sm:grid-cols-7 gap-1.5 p-2 rounded-xl max-h-32 overflow-y-auto ${isDarkMode ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
                      {emojis.map(e => {
                        const key = pickerKeyOf(e);
                        const selected = newMarker.emoji === key;
                        return (
                          <button
                            key={e.id}
                            onClick={() => setNewMarker({ ...newMarker, emoji: key })}
                            title={e.name || ''}
                            className={`aspect-square rounded-lg text-xl flex items-center justify-center overflow-hidden transition-all ${
                              selected
                                ? 'bg-blue-500 text-white scale-105 ring-2 ring-blue-400 ring-offset-1 ' + (isDarkMode ? 'ring-offset-gray-900' : 'ring-offset-white')
                                : isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-blue-50'
                            }`}
                          >
                            {e.image_url ? (
                              <img src={e.image_url} alt={e.name || ''} className="w-full h-full object-cover rounded-md" onError={(ev) => { ev.target.style.display = 'none'; }} />
                            ) : (
                              e.emoji
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <label className={`block text-[11px] font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {t('morocco_note_title_label')}
                    </label>
                    <input
                      type="text"
                      placeholder={t('morocco_note_title_placeholder')}
                      value={newMarker.title}
                      onChange={(e) => setNewMarker({ ...newMarker, title: e.target.value })}
                      className={`w-full px-3 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 ${
                        isDarkMode ? 'bg-gray-800 text-white border-gray-700 placeholder-gray-500 focus:ring-blue-500/40 focus:border-blue-500' : 'bg-white text-gray-900 border-gray-300 placeholder-gray-400 focus:ring-blue-500/30 focus:border-blue-500'
                      }`}
                    />
                  </div>

                  {/* Content */}
                  <div>
                    <label className={`block text-[11px] font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {t('morocco_note_content_label')} <span className="opacity-60 font-normal normal-case">{t('morocco_note_optional')}</span>
                    </label>
                    <textarea
                      placeholder={t('morocco_note_content_placeholder')}
                      value={newMarker.content}
                      onChange={(e) => setNewMarker({ ...newMarker, content: e.target.value })}
                      rows={3}
                      className={`w-full px-3 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 ${
                        isDarkMode ? 'bg-gray-800 text-white border-gray-700 placeholder-gray-500 focus:ring-blue-500/40 focus:border-blue-500' : 'bg-white text-gray-900 border-gray-300 placeholder-gray-400 focus:ring-blue-500/30 focus:border-blue-500'
                      }`}
                    />
                  </div>
                </div>

                {/* ── Footer ─────────────────────────────────── */}
                <div className={`flex gap-2 px-4 py-3 border-t ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-100 bg-gray-50/60'}`}>
                  <button
                    onClick={() => { setShowEmojiPicker(false); setSelectedPosition(null); }}
                    className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                      isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={createMarker}
                    disabled={!newMarker.title}
                    className="flex-1 px-4 py-2 text-sm font-bold rounded-lg text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-md disabled:opacity-40 disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-400 disabled:shadow-none transition-all"
                  >
                    {t('morocco_save_note_button')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Phrases / Shop / Budget content — full width when active, hidden when Markers tab is active */}
        <div className={`${activeTab === 'markers' ? 'hidden' : 'flex'} flex-1 w-full flex-col overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          {/* Stat cards row + body in a 2-column layout (content left, Activity sidebar right) */}
          <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          {/* Main content column */}
          <div className="flex-1 overflow-y-auto p-4 pb-24 lg:pb-6 lg:px-6 lg:pt-5">
            {/* STAT CARDS ROW — Design B: 5 cards (Phrases, Markers, Categories, Budget, Today) */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4 lg:mb-5">
              {(() => {
                const todayStr = new Date().toISOString().split('T')[0];
                const yesterdayStr = new Date(Date.now() - 86400000).toISOString().split('T')[0];
                const phrasesToday = phrases.filter(p => (p.created_at || '').startsWith(todayStr)).length;
                const phrasesYesterday = phrases.filter(p => (p.created_at || '').startsWith(yesterdayStr)).length;
                const markersToday = markers.filter(m => (m.created_at || '').startsWith(todayStr)).length;
                const growth = phrasesYesterday > 0
                  ? Math.round(((phrasesToday - phrasesYesterday) / phrasesYesterday) * 100)
                  : (phrasesToday > 0 ? 100 : 0);
                const stats = [
                  { label: t('morocco_tab_phrases'),    value: phrases.length,    delta: phrasesToday > 0 ? `+${phrasesToday} ↗` : null, trend: [3,5,4,7,6,8,9], accent: 'blue' },
                  { label: t('morocco_tab_markers'),    value: markers.length,    delta: markersToday > 0 ? `+${markersToday} ↗` : null, trend: [1,2,2,3,4,4,5], accent: 'green' },
                  { label: t('morocco_stat_categories'), value: categories.length, delta: null,                                              trend: [2,2,3,3,4,4,5], accent: 'purple' },
                  { label: t('morocco_tab_budget'),     value: budgetStats && typeof budgetStats.total === 'number' ? `${Math.round(budgetStats.total)} MAD` : '0 MAD', delta: null, trend: [10,12,8,15,11,14,13], accent: 'red' },
                  { label: t('morocco_stat_today'),      value: phrasesToday,
                    delta: phrasesToday === 0
                      ? t('morocco_streak_start')
                      : (growth > 0 ? `${t('morocco_grew_prefix')} ${growth}% 🚀` : growth < 0 ? `${Math.abs(growth)}% ${t('morocco_slower_suffix')}` : t('morocco_steady_today')),
                    trend: [2,3,2,4,3,5,phrasesToday || 1], accent: 'amber' },
                ];
                const accentClasses = {
                  blue:   { bar: 'bg-blue-500',   text: 'text-blue-600' },
                  green:  { bar: 'bg-emerald-500', text: 'text-emerald-600' },
                  purple: { bar: 'bg-purple-500', text: 'text-purple-600' },
                  red:    { bar: 'bg-rose-500',   text: 'text-rose-600' },
                  amber:  { bar: 'bg-amber-500',  text: 'text-amber-600' },
                };
                return stats.map((s, i) => {
                  const accent = accentClasses[s.accent];
                  const maxVal = Math.max(...s.trend, 1);
                  return (
                    <div key={i} className={`rounded-2xl p-3 lg:p-4 shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className={`text-[10px] uppercase tracking-wider font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {s.label}
                        </div>
                        {/* Mini sparkline: 7 bars */}
                        <div className="flex items-end gap-0.5 h-6">
                          {s.trend.map((v, j) => (
                            <div key={j} className={`${accent.bar} rounded-sm opacity-80`} style={{ width: '3px', height: `${Math.max(15, (v / maxVal) * 100)}%` }} />
                          ))}
                        </div>
                      </div>
                      <div className={`text-2xl lg:text-3xl font-extrabold leading-none ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {s.value}
                      </div>
                      {s.delta && (
                        <div className={`text-[11px] font-bold mt-1 leading-tight ${accent.text}`}>{s.delta}</div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
            {/* PHRASES TAB */}
            {activeTab === 'phrases' && (
              <div className="space-y-3">
                {/* Dictionary Title — demo style: centered, color-coded, extrabold */}
                <div className="text-center pb-1">
                  <h2 className={`text-base font-extrabold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    📖 {t('morocco_phrases_title')}
                  </h2>
                </div>
                
                {/* Top Bar: Add Button + Search — demo style (slate-900 button, full-width rounded search) */}
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  <button
                    onClick={() => setShowAddPhrase(!showAddPhrase)}
                    className={`w-full sm:w-auto h-11 px-4 rounded-2xl text-sm font-bold flex items-center justify-center gap-1.5 shadow-md transition-colors ${
                      showAddPhrase
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                  >
                    {showAddPhrase ? `✕ ${t('close')}` : `➕ ${t('add_new')}`}
                  </button>
                  <div className="flex-1 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                    <input
                      type="text"
                      placeholder={t('search_phrases')}
                      value={phraseSearch}
                      onChange={(e) => setPhraseSearch(e.target.value)}
                      className={`w-full h-11 pl-10 pr-9 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                        isDarkMode ? 'bg-gray-800 text-white border border-gray-700 placeholder-gray-400' : 'bg-white border border-gray-200 placeholder-gray-400'
                      }`}
                    />
                    {phraseSearch && (
                      <button
                        onClick={() => setPhraseSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Collapsible Add Form - Mini Dictionary Style */}
                {showAddPhrase && (
                  <div className={`p-4 rounded-lg border-2 border-dashed ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="space-y-3">
                      {/* Language Selection - Category First */}
                      <div className="flex gap-2">
                        <select
                          value={newPhrase.category}
                          onChange={(e) => setNewPhrase({ ...newPhrase, category: e.target.value })}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                        >
                          <option value="">{`📚 ${t('select_category_first')}`}</option>
                          {categories.filter(c => c.type === 'phrases').map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                          ))}
                        </select>
                        <select
                          value={newPhrase.city_id}
                          onChange={(e) => setNewPhrase({ ...newPhrase, city_id: e.target.value })}
                          className={`w-32 px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                        >
                          <option value="">📍 City</option>
                          {cities.map(city => (
                            <option key={city.id} value={city.id}>{city.name}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Dynamic Language Inputs based on settings */}
                      <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-white border'}`}>
                        <div className="grid grid-cols-1 gap-2">
                          {/* Default fields + custom fields from settings */}
                          {(settings.phrase_fields && settings.phrase_fields.length > 0 
                            ? settings.phrase_fields 
                            : [
                                { id: 'text_english', name: 'English', icon: '🇬🇧' },
                                { id: 'text_darija', name: 'Darija', icon: '🇲🇦' },
                                { id: 'text_third_lang', name: 'Other', icon: '🗣️' }
                              ]
                          ).map((field) => {
                            // Determine the value key for state
                            const getStateKey = () => {
                              if (field.id === 'text_english') return 'english';
                              if (field.id === 'text_darija') return 'darija';
                              if (field.id === 'text_third_lang') return 'dialect';
                              return field.id; // custom fields
                            };
                            const stateKey = getStateKey();
                            
                            // Get icon based on category for third lang
                            const displayIcon = field.id === 'text_third_lang' 
                              ? (newPhrase.category === 'amazigh' ? 'ⵣ' : 
                                 newPhrase.category === 'north_dialect' ? '🌊' : 
                                 field.icon)
                              : field.icon;
                            
                            const maxChars = settings.max_phrase_chars || 300;
                            const currentLen = (newPhrase[stateKey] || '').length;
                            const nearLimit = currentLen >= Math.max(10, maxChars - 20);
                            return (
                              <div key={field.id} className="flex items-start gap-2">
                                <span className={`w-20 text-xs font-semibold pt-2 ${
                                  field.id === 'text_english' ? 'text-blue-500' :
                                  field.id === 'text_darija' ? 'text-green-500' : 'text-purple-500'
                                }`}>
                                  {displayIcon} {field.name}
                                </span>
                                <div className="flex-1">
                                  <input
                                    type="text"
                                    placeholder={`Enter ${field.name}...`}
                                    value={newPhrase[stateKey] || ''}
                                    onChange={(e) => setNewPhrase({ ...newPhrase, [stateKey]: e.target.value.slice(0, maxChars) })}
                                    maxLength={maxChars}
                                    className={`w-full px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-50 border'}`}
                                  />
                                  {/* Helper text for the "My Language" native-language slot — clarifies the field
                                      is for the user's own language. Match by FIELD NAME so it works with the admin's
                                      custom field (which is not the legacy text_third_lang id). */}
                                  {field.name && field.name.toLowerCase().trim() === 'my language' && (
                                    <div className={`text-[10px] mt-0.5 italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Your native language — write the word as you'd remember it. e.g. a Spanish speaker writes <span className="font-semibold">Hola</span> for <span className="font-semibold">Hello</span>.
                                    </div>
                                  )}
                                  <div className={`text-[10px] mt-0.5 text-right ${nearLimit ? 'text-red-500 font-semibold' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {currentLen} / {maxChars}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      <button
                        onClick={() => {
                          createPhrase();
                          setShowAddPhrase(false);
                        }}
                        disabled={!newPhrase.english || !newPhrase.darija || !newPhrase.category}
                        className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 text-sm font-medium"
                      >
                        {!newPhrase.category ? `⚠️ ${t('select_category_first')}` : `✓ ${t('add_new')}`}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Edit Phrase Modal - 3 Language Format */}
                {editingPhrase && (
                  <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'bg-yellow-900/30 border-yellow-600' : 'bg-yellow-50 border-yellow-300'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <span className={`font-medium text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                        ✏️ Editing Phrase
                      </span>
                      <button
                        onClick={() => setEditingPhrase(null)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="space-y-2">
                      {/* Dynamic Language Inputs from Admin Settings */}
                      <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-white border'}`}>
                        <div className="grid grid-cols-1 gap-2">
                          {(settings.phrase_fields || []).map(field => {
                            const maxChars = settings.max_phrase_chars || 300;
                            const currentLen = (editingPhrase[field.id] || '').length;
                            const nearLimit = currentLen >= Math.max(10, maxChars - 20);
                            return (
                              <div key={field.id} className="flex items-start gap-2">
                                <span className="w-24 text-xs font-semibold text-blue-500 truncate pt-2">
                                  {field.icon} {field.name}
                                </span>
                                <div className="flex-1">
                                  <input
                                    type="text"
                                    placeholder={`Enter ${field.name}...`}
                                    value={editingPhrase[field.id] || ''}
                                    onChange={(e) => setEditingPhrase({ ...editingPhrase, [field.id]: e.target.value.slice(0, maxChars) })}
                                    maxLength={maxChars}
                                    className={`w-full px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-50 border'}`}
                                  />
                                  {field.name && field.name.toLowerCase().trim() === 'my language' && (
                                    <div className={`text-[10px] mt-0.5 italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Your native language — write the word as you'd remember it. e.g. a Spanish speaker writes <span className="font-semibold">Hola</span> for <span className="font-semibold">Hello</span>.
                                    </div>
                                  )}
                                  <div className={`text-[10px] mt-0.5 text-right ${nearLimit ? 'text-red-500 font-semibold' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {currentLen} / {maxChars}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={editingPhrase.category || ''}
                          onChange={(e) => setEditingPhrase({ ...editingPhrase, category: e.target.value })}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                        >
                          <option value="">{t('morocco_stat_categories')}</option>
                          {categories.filter(c => c.type === 'phrases').map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                          ))}
                        </select>
                        <select
                          value={editingPhrase.city_id || ''}
                          onChange={(e) => setEditingPhrase({ ...editingPhrase, city_id: e.target.value })}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                        >
                          <option value="">City (optional)</option>
                          {cities.map(city => (
                            <option key={city.id} value={city.id}>{city.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => updatePhrase(editingPhrase.id)}
                          className="flex-1 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm font-medium"
                        >
                          ✓ Save Changes
                        </button>
                        <button
                          onClick={() => setEditingPhrase(null)}
                          className={`px-4 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'}`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Filter pills — Minimal Clean style */}
                <div className="flex flex-nowrap overflow-x-auto sm:flex-wrap gap-1.5 -mx-4 px-4 pb-1 sm:mx-0 sm:px-0 sm:pb-0" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  <button
                    onClick={() => setPhraseFilter('all')}
                    className={`px-3.5 h-8 rounded-full text-xs font-bold transition-all whitespace-nowrap shrink-0 ${
                      phraseFilter === 'all'
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-sm'
                        : isDarkMode ? 'bg-gray-800 text-gray-200 border border-gray-700 hover:bg-gray-700' : 'bg-white text-gray-700 border border-gray-100 shadow-sm hover:bg-gray-50'
                    }`}
                  >
                    All · {phrases.length}
                  </button>
                  {categories.filter(c => c.type === 'phrases').map(cat => {
                    const count = phrases.filter(p => p.category === cat.id).length;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setPhraseFilter(cat.id)}
                        className={`pl-2.5 pr-3.5 h-8 rounded-full text-xs font-bold transition-all whitespace-nowrap shrink-0 inline-flex items-center gap-1.5 ${
                          phraseFilter === cat.id
                            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-sm'
                            : isDarkMode ? 'bg-gray-800 text-gray-200 border border-gray-700 hover:bg-gray-700' : 'bg-white text-gray-700 border border-gray-100 shadow-sm hover:bg-gray-50'
                        }`}
                      >
                        {cat.image_url ? (
                          <img src={cat.image_url} alt="" className="w-4 h-4 rounded object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          <span>{cat.icon}</span>
                        )}
                        <span>{cat.name} · {count}</span>
                      </button>
                    );
                  })}
                </div>
                
                {/* Phrases List - Grouped by Category */}
                <div className="space-y-2">
                  {(() => {
                    // Filter phrases based on search and filter
                    let filteredPhrases = phrases;
                    
                    // Apply search - supports both new (text_english, text_darija, text_third_lang) and old (text, translation) fields
                    if (phraseSearch) {
                      const searchLower = phraseSearch.toLowerCase();
                      filteredPhrases = filteredPhrases.filter(p => 
                        (p.text_english && p.text_english.toLowerCase().includes(searchLower)) ||
                        (p.text_darija && p.text_darija.toLowerCase().includes(searchLower)) ||
                        (p.text_third_lang && p.text_third_lang.toLowerCase().includes(searchLower)) ||
                        (p.text && p.text.toLowerCase().includes(searchLower)) ||
                        (p.translation && p.translation.toLowerCase().includes(searchLower))
                      );
                    }
                    
                    // Apply category filter
                    if (phraseFilter !== 'all') {
                      filteredPhrases = filteredPhrases.filter(p => p.category === phraseFilter);
                    }

                    // Sort by display_order asc (user reordering), then created_at desc
                    filteredPhrases = [...filteredPhrases].sort((a, b) => {
                      const aOrder = a.display_order ?? 9999;
                      const bOrder = b.display_order ?? 9999;
                      if (aOrder !== bOrder) return aOrder - bOrder;
                      return (b.created_at || '').localeCompare(a.created_at || '');
                    });

                    if (filteredPhrases.length === 0) {
                      return (
                        <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {phrases.length === 0
                            ? t('no_phrases_yet')
                            : t('no_phrases_match')}
                        </p>
                      );
                    }
                    
                    // Group phrases by category
                    const groupedByCategory = {};
                    
                    filteredPhrases.forEach(phrase => {
                      const catId = phrase.category || 'no_category';
                      if (!groupedByCategory[catId]) {
                        groupedByCategory[catId] = [];
                      }
                      groupedByCategory[catId].push(phrase);
                    });
                    
                    const toggleCategory = (catId) => {
                      setExpandedCategories(prev => ({
                        ...prev,
                        [catId]: !prev[catId]
                      }));
                    };
                    
                    return (
                      <>
                        {/* Category Groups */}
                        {Object.entries(groupedByCategory).map(([catId, catPhrases]) => {
                          const category = categories.find(c => c.id === catId);
                          const catIcon = category?.icon || '📝';
                          const catName = category?.name || t('no_category');
                          
                          return (
                            <div key={catId} className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-100'}`}>
                              <button
                                onClick={() => toggleCategory(catId)}
                                className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${
                                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="text-base">{catIcon}</span>
                                  <span className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{catName}</span>
                                  <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{catPhrases.length}</span>
                                </div>
                                <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{expandedCategories[catId] !== false ? '▼' : '▶'}</span>
                              </button>
                              {expandedCategories[catId] !== false && (
                                <div className="p-3 pt-0 space-y-2.5">
                                  {catPhrases.map((phrase, idx) => (
                                    <PhraseCard
                                      key={phrase.id}
                                      phrase={phrase}
                                      isDarkMode={isDarkMode}
                                      phraseFields={settings.phrase_fields || []}
                                      onEdit={() => {
                                        // Map old fields to new fields for backward compatibility
                                        setEditingPhrase({
                                          ...phrase,
                                          text_english: phrase.text_english || phrase.text || '',
                                          text_darija: phrase.text_darija || phrase.translation || '',
                                          text_third_lang: phrase.text_third_lang || phrase.family_member || ''
                                        });
                                      }}
                                      onDelete={() => confirmDeletePhrase(phrase)}
                                      onMoveUp={idx > 0 ? () => movePhrase(phrase.id, 'up') : null}
                                      onMoveDown={idx < catPhrases.length - 1 ? () => movePhrase(phrase.id, 'down') : null}
                                      getCityName={getCityName}
                                      getCategoryName={getCategoryName}
                                      compact={true}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
            
            {/* SHOPPING TAB - List-based organization */}
            {activeTab === 'shopping' && (
              <div className="space-y-4">
                {/* Section Title — demo style: centered, color-coded, semi-bold */}
                <div className="text-center pb-1">
                  <h2 className={`text-base font-extrabold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    🛒 {t('morocco_shopping_title')}
                  </h2>
                </div>

                {/* Sub-navigation pill bar — Minimal Clean style */}
                {!currentList && (
                  <div className={`flex gap-1 p-1 mx-auto rounded-2xl shadow-sm w-fit ${isDarkMode ? 'bg-gray-800' : 'bg-white border border-gray-100'}`}>
                    {[
                      { id: 'lists', icon: '📋', labelKey: 'morocco_my_lists' },
                      { id: 'stats', icon: '📊', labelKey: 'morocco_insights' },
                      { id: 'wishlist', icon: '⭐', labelKey: 'morocco_wishlist' }
                    ].map(view => (
                      <button
                        key={view.id}
                        onClick={() => {
                          setShoppingView(view.id);
                          if (view.id === 'stats') loadShoppingStats();
                          if (view.id === 'wishlist') loadWishlist();
                        }}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1 transition-all ${
                          shoppingView === view.id
                            ? 'bg-slate-900 text-white shadow-sm'
                            : isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <span>{view.icon}</span>
                        {t(view.labelKey)}
                      </button>
                    ))}
                  </div>
                )}
                
                {/* View: Inside a List */}
                {currentList ? (
                  <div className="space-y-3">
                    {/* List Header */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => { setCurrentList(null); setListItems([]); }}
                        className={`flex items-center gap-1 text-sm ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                      >
                        ← {t('back_to_lists')}
                      </button>
                      <button
                        onClick={() => confirmDeleteList(currentList)}
                        className="text-xs text-red-500 hover:text-red-600"
                      >
                        🗑️ {t('delete_list')}
                      </button>
                    </div>
                    
                    {/* List Info */}
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gradient-to-r from-purple-900/50 to-blue-900/50' : 'bg-gradient-to-r from-purple-100 to-blue-100'}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{currentList.icon}</span>
                        <div>
                          <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {currentList.name}
                          </h3>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {listItems.filter(i => i.is_bought).length}/{listItems.length} {t('items')}
                          </p>
                          {(() => {
                            const totalSpent = listItems.filter(i => i.is_bought).reduce((sum, i) => sum + (i.price || 0), 0);
                            if (totalSpent > 0) {
                              return (
                                <div className="mt-1">
                                  <PriceDisplay price={totalSpent} currencyRates={currencyRates} isDarkMode={isDarkMode} size="md" />
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    </div>
                    
                    {/* Add Item Button/Form */}
                    {!showAddItem ? (
                      <button
                        onClick={() => setShowAddItem(true)}
                        className={`w-full py-3 rounded-lg border-2 border-dashed font-medium transition-colors ${
                          isDarkMode 
                            ? 'border-gray-600 text-gray-400 hover:border-green-500 hover:text-green-400' 
                            : 'border-gray-300 text-gray-500 hover:border-green-500 hover:text-green-600'
                        }`}
                      >
                        {t('morocco_add_item')}
                      </button>
                    ) : (
                      <div className={`p-4 rounded-lg border-2 border-dashed overflow-hidden ${isDarkMode ? 'bg-gray-700 border-green-600' : 'bg-green-50 border-green-300'}`}>
                        <div className="space-y-3">
                          {/* Item Name */}
                          <div>
                            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {t('morocco_item_name_label')}
                            </label>
                            <input
                              type="text"
                              placeholder={t('morocco_item_name_placeholder')}
                              value={newItem.name}
                              onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                              className={`w-full px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                              autoFocus
                            />
                          </div>
                          
                          {/* Quantity & Unit */}
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                🔢 Quantity
                              </label>
                              <input
                                type="number"
                                placeholder={t('how_many')}
                                min="1"
                                value={newItem.quantity}
                                onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 1 })}
                                className={`w-full px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                              />
                            </div>
                            <div className="flex-1">
                              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                📏 Unit
                              </label>
                              <select
                                value={newItem.unit}
                                onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                                className={`w-full px-2 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                              >
                                <optgroup label="Count">
                                  <option value="items">items</option>
                                  <option value="pieces">pieces</option>
                                  <option value="pairs">pairs</option>
                                  <option value="sets">sets</option>
                                  <option value="boxes">boxes</option>
                                  <option value="packs">packs</option>
                                </optgroup>
                                <optgroup label="Weight">
                                  <option value="kg">kg</option>
                                  <option value="g">grams</option>
                                  <option value="lbs">lbs</option>
                                </optgroup>
                                <optgroup label="Volume">
                                  <option value="L">liters</option>
                                  <option value="ml">ml</option>
                                </optgroup>
                              </select>
                            </div>
                          </div>
                          
                          {/* Price & Currency */}
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                💰 Price
                              </label>
                              <input
                                type="number"
                                placeholder={t('morocco_price_zero_placeholder')}
                                min="0"
                                step="0.01"
                                value={newItem.price}
                                onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                                className={`w-full px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                              />
                            </div>
                            <div className="w-24">
                              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                💱 Currency
                              </label>
                              <select
                                value={newItem.currency}
                                onChange={(e) => setNewItem({ ...newItem, currency: e.target.value })}
                                className={`w-full px-2 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                              >
                                <option value="MAD">MAD (د.م.)</option>
                                <option value="EUR">€ Euro</option>
                                <option value="USD">$ Dollar</option>
                                <option value="GBP">£ Pound</option>
                              </select>
                            </div>
                          </div>
                          
                          {/* City */}
                          <div>
                            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              📍 City (optional)
                            </label>
                            <select
                              value={newItem.city_id}
                              onChange={(e) => setNewItem({ ...newItem, city_id: e.target.value })}
                              className={`w-full px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                            >
                              <option value="">{t('morocco_select_a_city')}</option>
                              {cities.map(city => (
                                <option key={city.id} value={city.id}>{city.name}</option>
                              ))}
                            </select>
                          </div>
                          
                          {/* Note */}
                          <div>
                            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              📝 Note (optional)
                            </label>
                            <input
                              type="text"
                              placeholder={t('morocco_item_note_placeholder')}
                              value={newItem.note}
                              onChange={(e) => setNewItem({ ...newItem, note: e.target.value })}
                              className={`w-full px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                            />
                          </div>
                          
                          {/* Buttons */}
                          <div className="flex gap-2 pt-2">
                            <button
                              onClick={createItem}
                              disabled={!newItem.name}
                              className="flex-1 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50"
                            >
                              ✓ Add Item
                            </button>
                            <button
                              onClick={() => { setShowAddItem(false); setNewItem({ name: '', quantity: 1, unit: 'items', price: '', currency: 'MAD', note: '', city_id: '' }); }}
                              className={`px-4 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'}`}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Items List */}
                    <div className="space-y-1">
                      {listItems.length === 0 ? (
                        <p className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          No items yet. Add your first item!
                        </p>
                      ) : (
                        listItems.map(item => (
                          <div 
                            key={item.id}
                            className={`p-3 rounded-lg flex items-center gap-3 transition-all ${
                              item.is_bought 
                                ? isDarkMode ? 'bg-green-900/30' : 'bg-green-50'
                                : isDarkMode ? 'bg-gray-700' : 'bg-white border'
                            }`}
                          >
                            {/* Checkbox */}
                            <button
                              onClick={() => toggleItemBought(item.id)}
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                item.is_bought 
                                  ? 'bg-green-500 border-green-500 text-white' 
                                  : isDarkMode ? 'border-gray-500 hover:border-green-500' : 'border-gray-300 hover:border-green-500'
                              }`}
                            >
                              {item.is_bought && '✓'}
                            </button>
                            
                            {/* Item Info */}
                            <div className={`flex-1 min-w-0 ${item.is_bought ? 'opacity-60' : ''}`}>
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${item.is_bought ? 'line-through' : ''} ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                  {item.name}
                                </span>
                                {(item.quantity > 0) && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                                    {item.quantity} {item.unit || 'pc'}
                                  </span>
                                )}
                              </div>
                              {/* Price with currency conversion */}
                              {item.price > 0 && (
                                <PriceDisplay price={item.price} currencyRates={currencyRates} isDarkMode={isDarkMode} size="sm" />
                              )}
                              <div className="flex items-center gap-2 text-xs">
                                {item.city_id && (
                                  <span className={isDarkMode ? 'text-blue-400' : 'text-blue-600'}>
                                    📍 {getCityName(item.city_id)}
                                  </span>
                                )}
                                {item.note && (
                                  <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                                    {item.note}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex gap-1 flex-shrink-0">
                              <button
                                onClick={() => setEditingItem(item)}
                                className={`p-1 rounded hover:scale-110 transition-transform ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => confirmDeleteItem(item)}
                                className={`p-1 rounded hover:scale-110 transition-transform ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ) : (
                  /* View: Lists Home */
                  shoppingView === 'lists' && (
                  <div className="space-y-3">
                    {/* Search Bar - Full Width */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder={t('morocco_search_lists_placeholder')}
                        value={listSearch}
                        onChange={(e) => setListSearch(e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg text-sm ${
                          isDarkMode ? 'bg-gray-700 text-white placeholder-gray-400' : 'bg-white border placeholder-gray-500'
                        }`}
                      />
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setShowCreateList(!showCreateList); setShowCalculator(false); }}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          showCreateList
                            ? 'bg-red-500 text-white hover:bg-red-600'
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                      >
                        {showCreateList ? `✕ ${t('cancel')}` : t('morocco_new_list')}
                      </button>
                      <button
                        onClick={() => { setShowCalculator(!showCalculator); setShowCreateList(false); }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          showCalculator
                            ? 'bg-blue-500 text-white'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                        }`}
                        title={t('morocco_calculator_tooltip')}
                      >
                        🧮
                      </button>
                    </div>
                    
                    {/* Quick Calculator */}
                    {showCalculator && (
                      <QuickCalculator isDarkMode={isDarkMode} currencyRates={currencyRates} />
                    )}
                    
                    {/* Create List Form */}
                    {showCreateList && (
                      <div className={`p-4 rounded-lg border-2 border-dashed ${isDarkMode ? 'bg-gray-700 border-green-600' : 'bg-green-50 border-green-300'}`}>
                        <div className="space-y-3">
                          {/* Step 1: Name */}
                          <div>
                            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              ① List Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              placeholder={t('morocco_list_name_placeholder')}
                              value={newList.name}
                              onChange={(e) => setNewList({ ...newList, name: e.target.value })}
                              className={`w-full px-3 py-2.5 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white placeholder-gray-400' : 'bg-white border placeholder-gray-400'}`}
                              autoFocus
                            />
                          </div>
                          
                          {/* Step 2: Category */}
                          <div>
                            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              ② Pick Category
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                              {listCategories.map(cat => (
                                <button
                                  key={cat.id}
                                  onClick={() => setNewList({ ...newList, category: cat.id })}
                                  className={`p-2 rounded-lg text-center transition-all ${
                                    newList.category === cat.id
                                      ? 'bg-blue-500 text-white scale-105 ring-2 ring-blue-300'
                                      : isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-100 hover:bg-gray-200'
                                  }`}
                                >
                                  {cat.image_url ? (
                                    <img src={cat.image_url} alt="" className="w-7 h-7 mx-auto rounded object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                  ) : (
                                    <div className="text-xl">{cat.icon}</div>
                                  )}
                                  <div className="text-xs mt-1">{cat.name}</div>
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Step 3: Create */}
                          <button
                            onClick={createList}
                            disabled={!newList.name.trim() || !newList.category}
                            className={`w-full py-2.5 rounded-lg font-medium transition-all ${
                              newList.name.trim() && newList.category
                                ? 'bg-green-500 text-white hover:bg-green-600'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            {!newList.category ? `⚠️ ${t('select_category_first')}` : newList.name.trim() ? `✓ ${t('create_list')}` : t('enter_name_first')}
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Lists Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      {shoppingLists
                        .filter(l => !listSearch || l.name.toLowerCase().includes(listSearch.toLowerCase()))
                        .map(list => (
                          <button
                            key={list.id}
                            onClick={() => loadListItems(list.id)}
                            className={`p-4 rounded-xl text-left transition-all hover:scale-[1.02] ${
                              isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white border hover:shadow-md'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <span className="text-2xl">{list.icon}</span>
                              <div className="flex-1 min-w-0">
                                <h4 className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                  {list.name}
                                </h4>
                                <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                                  {list.bought_items}/{list.total_items} items
                                </p>
                                {list.total_spent > 0 && (
                                  <p className="text-xs text-green-500 font-medium">
                                    {list.total_spent.toFixed(0)} DH
                                  </p>
                                )}
                              </div>
                            </div>
                            {/* Progress bar */}
                            {list.total_items > 0 && (
                              <div className={`mt-2 h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                                <div 
                                  className="h-full bg-green-500 transition-all"
                                  style={{ width: `${(list.bought_items / list.total_items) * 100}%` }}
                                />
                              </div>
                            )}
                          </button>
                        ))}
                    </div>
                    
                    {/* Empty State */}
                    {shoppingLists.length === 0 && !showCreateList && (
                      <div className={`text-center py-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        <div className="text-5xl mb-4">🛒</div>
                        <p className="font-medium">{t('no_shopping_lists')}</p>
                        <p className="text-sm">{t('create_first_list')}</p>
                      </div>
                    )}
                  </div>
                ))}
                
                {/* View: Stats & Insights */}
                {!currentList && shoppingView === 'stats' && (
                  <div className="space-y-4">
                    {/* Loading state */}
                    {!shoppingStats ? (
                      <div className={`text-center py-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        <div className="text-4xl mb-2">📊</div>
                        <p>{t('loading_insights')}</p>
                      </div>
                    ) : (
                      <>
                        {/* Overview Cards */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-green-900/50 to-emerald-900/50' : 'bg-gradient-to-br from-green-100 to-emerald-100'}`}>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('total_spent')}</p>
                            <PriceDisplay price={shoppingStats.total_spent || 0} currencyRates={currencyRates} isDarkMode={isDarkMode} size="lg" />
                          </div>
                          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-blue-900/50 to-indigo-900/50' : 'bg-gradient-to-br from-blue-100 to-indigo-100'}`}>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('items_bought')}</p>
                            <p className={`text-2xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              {shoppingStats.total_items || 0}
                            </p>
                          </div>
                        </div>
                        
                        {/* Cheapest City */}
                        {shoppingStats.cheapest_city && (
                          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                            <div className="flex items-center gap-3">
                              <span className="text-3xl">🏆</span>
                              <div>
                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('your_cheapest_city')}</p>
                                <p className={`text-lg font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                                  {getCityName(shoppingStats.cheapest_city)}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Spending by Category */}
                        {shoppingStats.category_spending && Object.keys(shoppingStats.category_spending).length > 0 && (
                          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-white border'}`}>
                            <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              📈 {t('spending_by_category')}
                            </h4>
                            <div className="space-y-2">
                              {Object.entries(shoppingStats.category_spending)
                                .sort((a, b) => b[1] - a[1])
                                .map(([cat, amount]) => {
                                  const catInfo = listCategories.find(c => c.id === cat) || { icon: '📦', name: cat };
                                  const maxAmount = Math.max(...Object.values(shoppingStats.category_spending));
                                  const percent = maxAmount > 0 ? (amount / maxAmount) * 100 : 0;
                                  return (
                                    <div key={cat}>
                                      <div className="flex justify-between text-sm mb-1">
                                        <span>{catInfo.icon} {catInfo.name}</span>
                                        <span className={`font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{amount.toFixed(0)} DH</span>
                                      </div>
                                      <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                                        <div className="h-full bg-green-500 transition-all" style={{ width: `${percent}%` }} />
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                        
                        {/* Price Comparisons */}
                        {shoppingStats.price_comparisons && shoppingStats.price_comparisons.length > 0 && (
                          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-white border'}`}>
                            <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              💰 Price Comparisons
                            </h4>
                            <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Items you bought in multiple cities
                            </p>
                            <div className="space-y-3">
                              {shoppingStats.price_comparisons.map((comp, idx) => (
                                <div key={idx} className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}`}>
                                  <div className="flex justify-between items-start">
                                    <span className={`font-medium capitalize ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                      {comp.item}
                                    </span>
                                    <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                                      Save {comp.savings.toFixed(0)} DH
                                    </span>
                                  </div>
                                  <div className="flex gap-4 mt-2 text-sm">
                                    <div>
                                      <span className="text-green-500">🏆 {getCityName(comp.best_city)}</span>
                                      <span className={`ml-1 font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{comp.best_price} DH</span>
                                    </div>
                                    <div className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                                      vs {getCityName(comp.worst_city)} <span className="text-red-500">{comp.worst_price} DH</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Empty Stats State */}
                        {shoppingStats.total_items === 0 && (
                          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            <div className="text-4xl mb-2">📊</div>
                            <p className="font-medium">No data yet</p>
                            <p className="text-sm">Start shopping and mark items as bought to see insights!</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                
                {/* View: Wishlist */}
                {!currentList && shoppingView === 'wishlist' && (
                  <div className="space-y-3">
                    {/* Add Wishlist Item Button/Form */}
                    {!showAddWish ? (
                      <button
                        onClick={() => setShowAddWish(true)}
                        className={`w-full py-3 rounded-lg border-2 border-dashed font-medium transition-colors ${
                          isDarkMode 
                            ? 'border-gray-600 text-gray-400 hover:border-yellow-500 hover:text-yellow-400' 
                            : 'border-gray-300 text-gray-500 hover:border-yellow-500 hover:text-yellow-600'
                        }`}
                      >
                        ⭐ Add to Wishlist
                      </button>
                    ) : (
                      <div className={`p-4 rounded-lg border-2 border-dashed ${isDarkMode ? 'bg-gray-700 border-yellow-600' : 'bg-yellow-50 border-yellow-300'}`}>
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder={t('item_name')}
                            value={newWishItem.name}
                            onChange={(e) => setNewWishItem({ ...newWishItem, name: e.target.value })}
                            className={`w-full px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <input
                              type="number"
                              placeholder={t('target_price')}
                              value={newWishItem.target_price}
                              onChange={(e) => setNewWishItem({ ...newWishItem, target_price: e.target.value })}
                              className={`flex-1 px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                            />
                            <select
                              value={newWishItem.city_id}
                              onChange={(e) => setNewWishItem({ ...newWishItem, city_id: e.target.value })}
                              className={`flex-1 px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                            >
                              <option value="">{t('morocco_preferred_city_option')}</option>
                              {cities.map(city => (
                                <option key={city.id} value={city.id}>{city.name}</option>
                              ))}
                            </select>
                          </div>
                          <input
                            type="text"
                            placeholder={t('morocco_note_optional_placeholder')}
                            value={newWishItem.note}
                            onChange={(e) => setNewWishItem({ ...newWishItem, note: e.target.value })}
                            className={`w-full px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border'}`}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={addToWishlist}
                              disabled={!newWishItem.name}
                              className="flex-1 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 disabled:opacity-50"
                            >
                              ⭐ Add to Wishlist
                            </button>
                            <button
                              onClick={() => { setShowAddWish(false); setNewWishItem({ name: '', target_price: '', note: '', city_id: '' }); }}
                              className={`px-4 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'}`}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Wishlist Items */}
                    <div className="space-y-2">
                      {/* Active Wishlist */}
                      {wishlistItems.filter(i => !i.is_purchased).length > 0 && (
                        <>
                          <h4 className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            🎯 {t('want_to_buy')} ({wishlistItems.filter(i => !i.is_purchased).length})
                          </h4>
                          {wishlistItems.filter(i => !i.is_purchased).map(item => (
                            <WishlistCard 
                              key={item.id} 
                              item={item} 
                              isDarkMode={isDarkMode}
                              getCityName={getCityName}
                              cities={cities}
                              onPurchase={markWishPurchased}
                              onDelete={() => confirmDeleteWish(item)}
                            />
                          ))}
                        </>
                      )}
                      
                      {/* Purchased History */}
                      {wishlistItems.filter(i => i.is_purchased).length > 0 && (
                        <>
                          <h4 className={`text-sm font-medium mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {t('morocco_purchased')} ({wishlistItems.filter(i => i.is_purchased).length})
                          </h4>
                          {wishlistItems.filter(i => i.is_purchased).map(item => (
                            <div 
                              key={item.id}
                              className={`p-3 rounded-lg opacity-60 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <span className={`line-through ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.name}</span>
                                  <span className={`ml-2 text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                    {item.purchased_price} DH
                                  </span>
                                  {item.purchased_city && (
                                    <span className={`ml-1 text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                      📍 {getCityName(item.purchased_city)}
                                    </span>
                                  )}
                                </div>
                                <button
                                  onClick={() => confirmDeleteWish(item)}
                                  className="text-xs text-red-500 hover:text-red-600"
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                      
                      {/* Empty State */}
                      {wishlistItems.length === 0 && !showAddWish && (
                        <div className={`text-center py-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          <div className="text-5xl mb-4">⭐</div>
                          <p className="font-medium">{t('morocco_wishlist_empty_title')}</p>
                          <p className="text-sm">{t('morocco_wishlist_empty_desc')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Edit Item Modal */}
                {editingItem && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000]">
                    <div className={`p-5 rounded-xl max-w-sm w-full mx-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                      <h3 className={`font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {t('morocco_edit_item_modal')}
                      </h3>
                      <div className="space-y-3">
                        <input
                          type="text"
                          placeholder={t('item_name')}
                          value={editingItem.name}
                          onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                          className={`w-full px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 border'}`}
                        />
                        <div className="flex gap-2">
                          <input
                            type="number"
                            placeholder={t('morocco_qty_placeholder')}
                            value={editingItem.quantity}
                            onChange={(e) => setEditingItem({ ...editingItem, quantity: parseInt(e.target.value) || 1 })}
                            className={`w-20 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 border'}`}
                          />
                          <input
                            type="number"
                            placeholder={t('morocco_price_placeholder')}
                            value={editingItem.price}
                            onChange={(e) => setEditingItem({ ...editingItem, price: e.target.value })}
                            className={`flex-1 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 border'}`}
                          />
                        </div>
                        <select
                          value={editingItem.city_id || ''}
                          onChange={(e) => setEditingItem({ ...editingItem, city_id: e.target.value })}
                          className={`w-full px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 border'}`}
                        >
                          <option value="">📍 City (optional)</option>
                          {cities.map(city => (
                            <option key={city.id} value={city.id}>{city.name}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder={t('morocco_note_placeholder')}
                          value={editingItem.note || ''}
                          onChange={(e) => setEditingItem({ ...editingItem, note: e.target.value })}
                          className={`w-full px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 border'}`}
                        />
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => updateItem(editingItem.id)}
                            className="flex-1 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingItem(null)}
                            className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* BUDGET TAB */}
            {activeTab === 'budget' && (
              <div className="space-y-4">
                {/* Section Title — demo style */}
                <div className="text-center pb-1">
                  <h2 className={`text-base font-extrabold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                    💰 {t('morocco_budget_title')}
                  </h2>
                </div>

                {/* Sub-navigation pill bar — Minimal Clean style */}
                <div className={`flex gap-1 p-1 mx-auto rounded-2xl shadow-sm w-fit ${isDarkMode ? 'bg-gray-800' : 'bg-white border border-gray-100'}`}>
                  {[
                    { id: 'overview', icon: '📊', labelKey: 'overview' },
                    { id: 'expenses', icon: '📝', labelKey: 'expenses' },
                    { id: 'daily', icon: '📅', labelKey: 'daily' }
                  ].map(view => (
                    <button
                      key={view.id}
                      onClick={() => setBudgetView(view.id)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1 transition-all ${
                        budgetView === view.id
                          ? 'bg-slate-900 text-white shadow-sm'
                          : isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span>{view.icon}</span>
                      {t(view.labelKey)}
                    </button>
                  ))}
                </div>

                {/* Trip Budget Setup - Show if no budget set */}
                {(budgetSettings.total_budget === 0 || showBudgetSetup) && (
                  <BudgetSetupCard 
                    isDarkMode={isDarkMode} 
                    settings={budgetSettings}
                    onSave={saveBudgetSettings}
                    onCancel={() => setShowBudgetSetup(false)}
                  />
                )}
                
                {/* Daily Limit Alert */}
                {budgetStats.over_daily_limit && budgetSettings.daily_budget > 0 && (
                  <div className={`p-3 rounded-lg flex items-center gap-3 ${isDarkMode ? 'bg-red-900/50' : 'bg-red-100'}`}>
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <p className={`font-medium ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                        Over Daily Limit!
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                        You&apos;ve spent {budgetStats.today_spent?.toFixed(0)} DH today (limit: {budgetSettings.daily_budget?.toFixed(0)} DH)
                      </p>
                    </div>
                  </div>
                )}
                
                {/* OVERVIEW VIEW */}
                {budgetView === 'overview' && budgetSettings.total_budget > 0 && (
                  <div className="space-y-3">
                    {/* Budget Progress */}
                    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gradient-to-br from-green-900/50 to-blue-900/50' : 'bg-gradient-to-br from-green-100 to-blue-100'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Trip Budget</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowBudgetSetup(true)}
                            className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => setShowDeleteBudgetModal(true)}
                            className="text-xs text-red-500 hover:text-red-600"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-end mb-2">
                        <div>
                          <span className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {(budgetStats.remaining || 0).toFixed(0)}
                          </span>
                          <span className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}> DH left</span>
                        </div>
                        <div className={`text-right text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <p>{(budgetStats.total || 0).toFixed(0)} / {budgetSettings.total_budget} DH</p>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className={`h-3 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <div 
                          className={`h-full transition-all ${
                            (budgetStats.total / budgetSettings.total_budget) > 0.9 ? 'bg-red-500' :
                            (budgetStats.total / budgetSettings.total_budget) > 0.7 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min((budgetStats.total / budgetSettings.total_budget) * 100, 100)}%` }}
                        />
                      </div>
                      <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {budgetSettings.trip_days} day trip • {budgetSettings.daily_budget?.toFixed(0)} DH/day budget
                      </p>
                    </div>
                    
                    {/* Today's Spending */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-white border'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('morocco_stat_today')}</p>
                        <p className={`text-xl font-bold ${
                          budgetStats.over_daily_limit
                            ? 'text-red-500'
                            : isDarkMode ? 'text-white' : 'text-gray-800'
                        }`}>
                          {(budgetStats.today_spent || 0).toFixed(0)} DH
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          / {budgetSettings.daily_budget?.toFixed(0)} daily
                        </p>
                      </div>
                      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-white border'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('morocco_today_left')}</p>
                        <p className={`text-xl font-bold ${
                          (budgetStats.today_remaining || 0) < 0 
                            ? 'text-red-500' 
                            : isDarkMode ? 'text-green-400' : 'text-green-600'
                        }`}>
                          {(budgetStats.today_remaining || 0).toFixed(0)} DH
                        </p>
                      </div>
                    </div>
                    
                    {/* Category Breakdown */}
                    {budgetStats.by_category && Object.keys(budgetStats.by_category).length > 0 && (
                      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-white border'}`}>
                        <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {t('spending_by_category')}
                        </h4>
                        <div className="space-y-2">
                          {Object.entries(budgetStats.by_category)
                            .sort((a, b) => b[1] - a[1])
                            .map(([cat, amount]) => {
                              const catInfo = budgetCategories.find(c => c.id === cat) || { icon: '📦', name: cat };
                              const percent = budgetStats.total > 0 ? (amount / budgetStats.total) * 100 : 0;
                              return (
                                <div key={cat}>
                                  <div className="flex justify-between text-sm mb-1">
                                    <span>{catInfo.icon} {catInfo.name}</span>
                                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                                      {amount.toFixed(0)} DH ({percent.toFixed(0)}%)
                                    </span>
                                  </div>
                                  <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                                    <div className="h-full bg-blue-500" style={{ width: `${percent}%` }} />
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* EXPENSES VIEW */}
                {budgetView === 'expenses' && (
                  <div className="space-y-3">
                    {/* Add Expense Form */}
                    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-white border'}`}>
                      <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        ➕ {t('add_expense')}
                      </h4>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder={t('morocco_what_did_you_spend_on')}
                            value={newBudget.name}
                            onChange={(e) => setNewBudget({ ...newBudget, name: e.target.value })}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-100 border'}`}
                          />
                          <input
                            type="number"
                            placeholder={t('morocco_dh_placeholder')}
                            value={newBudget.amount}
                            onChange={(e) => setNewBudget({ ...newBudget, amount: e.target.value })}
                            className={`w-24 px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-100 border'}`}
                          />
                        </div>
                        <div className="flex gap-2">
                          <select
                            value={newBudget.category}
                            onChange={(e) => setNewBudget({ ...newBudget, category: e.target.value })}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-100 border'}`}
                          >
                            <option value="">{t('morocco_select_category')}</option>
                            {budgetCategories.map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                            ))}
                          </select>
                          <input
                            type="date"
                            value={newBudget.date}
                            onChange={(e) => setNewBudget({ ...newBudget, date: e.target.value })}
                            className={`px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-100 border'}`}
                          />
                        </div>
                        <button
                          onClick={createBudgetEntry}
                          disabled={!newBudget.name || !newBudget.amount || !newBudget.category}
                          className="w-full py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50"
                        >
                          {!newBudget.category ? `⚠️ ${t('select_category_first')}` : t('add_expense')}
                        </button>
                      </div>
                    </div>
                    
                    {/* Period Filter */}
                    <div className="flex gap-2">
                      {['all', 'daily', 'weekly', 'monthly'].map(p => (
                        <button
                          key={p}
                          onClick={() => setBudgetPeriod(p)}
                          className={`flex-1 py-1.5 rounded-lg text-xs font-medium ${
                            budgetPeriod === p
                              ? 'bg-blue-500 text-white'
                              : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {p === 'all' ? t('morocco_filter_all') : p === 'daily' ? t('morocco_filter_today') : p === 'weekly' ? t('morocco_filter_week') : t('morocco_filter_month')}
                        </button>
                      ))}
                    </div>
                    
                    {/* Expenses List */}
                    <div className="space-y-2">
                      {budgetEntries.length === 0 ? (
                        <div className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          <div className="text-4xl mb-2">📝</div>
                          <p>{t('morocco_no_expenses_yet')}</p>
                          <p className="text-sm">{t('morocco_add_first_expense')}</p>
                        </div>
                      ) : (
                        budgetEntries.map(entry => {
                          const catInfo = budgetCategories.find(c => c.id === entry.category) || { icon: '📦' };
                          return (
                            <div 
                              key={entry.id}
                              className={`p-3 rounded-lg flex items-center gap-3 ${isDarkMode ? 'bg-gray-700' : 'bg-white border'}`}
                            >
                              <span className="text-xl">{catInfo.icon}</span>
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                  {entry.name}
                                </p>
                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {entry.date ? new Date(entry.date).toLocaleDateString() : ''}
                                  {entry.source === 'shopping' && <span className="ml-1 text-blue-500">• from Shopping</span>}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className={`font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                  {entry.amount} DH
                                </p>
                              </div>
                              <button
                                onClick={() => deleteBudgetEntry(entry.id)}
                                className="p-1 text-red-500 hover:text-red-600"
                              >
                                🗑️
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
                
                {/* DAILY VIEW - With Daily, Weekly, Monthly sections */}
                {budgetView === 'daily' && (
                  <div className="space-y-4">
                    {budgetStats.daily_spending && Object.keys(budgetStats.daily_spending).length > 0 ? (
                      <>
                        {/* Daily Spending History */}
                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-white border'}`}>
                          <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {t('morocco_daily_spending_chart')}
                          </h4>
                          <div className="space-y-2">
                            {Object.entries(budgetStats.daily_spending)
                              .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                              .slice(0, 7)
                              .map(([date, amount]) => {
                                const usd = (amount * currencyRates.USD).toFixed(2);
                                const eur = (amount * currencyRates.EUR).toFixed(2);
                                const isOverLimit = budgetSettings.daily_budget > 0 && amount > budgetSettings.daily_budget;
                                return (
                                  <div key={date} className={`flex items-center justify-between py-2 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-100'} last:border-0`}>
                                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                      {new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                    </span>
                                    <div className="text-right">
                                      <span className={`font-bold ${isOverLimit ? 'text-red-500' : isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                        {amount.toFixed(0)} DH {isOverLimit && '⚠️'}
                                      </span>
                                      <span className={`text-xs ml-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        ${usd} • €{eur}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                        
                        {/* Weekly Spending History */}
                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-white border'}`}>
                          <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {t('morocco_weekly_spending_chart')}
                          </h4>
                          <div className="space-y-2">
                            {(() => {
                              // Group by week
                              const weeklyData = {};
                              Object.entries(budgetStats.daily_spending).forEach(([date, amount]) => {
                                const d = new Date(date);
                                const weekStart = new Date(d);
                                weekStart.setDate(d.getDate() - d.getDay());
                                const weekKey = weekStart.toISOString().slice(0, 10);
                                weeklyData[weekKey] = (weeklyData[weekKey] || 0) + amount;
                              });
                              return Object.entries(weeklyData)
                                .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                                .slice(0, 4)
                                .map(([weekStart, total], idx) => {
                                  const usd = (total * currencyRates.USD).toFixed(2);
                                  const eur = (total * currencyRates.EUR).toFixed(2);
                                  const weekEnd = new Date(weekStart);
                                  weekEnd.setDate(weekEnd.getDate() + 6);
                                  return (
                                    <div key={weekStart} className={`flex items-center justify-between py-2 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-100'} last:border-0`}>
                                      <div>
                                        <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('morocco_week_label')} {idx + 1}</span>
                                        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                          {new Date(weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </p>
                                      </div>
                                      <div className="text-right">
                                        <span className={`font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{total.toFixed(0)} DH</span>
                                        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>${usd} • €{eur}</p>
                                      </div>
                                    </div>
                                  );
                                });
                            })()}
                          </div>
                        </div>
                        
                        {/* Monthly Spending History */}
                        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-white border'}`}>
                          <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {t('morocco_monthly_spending_chart')}
                          </h4>
                          <div className="space-y-2">
                            {(() => {
                              // Group by month
                              const monthlyData = {};
                              Object.entries(budgetStats.daily_spending).forEach(([date, amount]) => {
                                const monthKey = date.slice(0, 7); // YYYY-MM
                                monthlyData[monthKey] = (monthlyData[monthKey] || 0) + amount;
                              });
                              return Object.entries(monthlyData)
                                .sort((a, b) => b[0].localeCompare(a[0]))
                                .slice(0, 3)
                                .map(([month, total]) => {
                                  const usd = (total * currencyRates.USD).toFixed(2);
                                  const eur = (total * currencyRates.EUR).toFixed(2);
                                  const monthDate = new Date(month + '-01');
                                  return (
                                    <div key={month} className={`flex items-center justify-between py-2 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-100'} last:border-0`}>
                                      <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                      </span>
                                      <div className="text-right">
                                        <span className={`font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{total.toFixed(0)} DH</span>
                                        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>${usd} • €{eur}</p>
                                      </div>
                                    </div>
                                  );
                                });
                            })()}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        <div className="text-4xl mb-2">📅</div>
                        <p>{t('morocco_no_expenses_yet')}</p>
                        <p className="text-sm">{t('morocco_add_expenses_to_see_history')}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Markers tab body was moved to a floating panel on the Map page (see the 📋 Markers
                toggle / markersPanelOpen panel inside the map wrapper above). The Markers tab now
                renders a full-bleed map without any content in this column. */}

            {/* 📖 GUIDE TAB — Morocco travel content cards (Emergency / Tipping / SIM / Cultural).
                Content authored in admin → 📖 Guide Cards. Each card has icon + title + plain-text content.
                Click a card header to expand; click again to collapse. */}
            {activeTab === 'guide' && (
              <div className="px-0 py-3 sm:p-4 lg:p-6 overflow-y-auto">
                <div className="max-w-3xl mx-auto space-y-2 sm:space-y-3">
                  {/* Header */}
                  <div className="mb-4">
                    <h2 className={`text-2xl font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>📖 {t('morocco_guide_title')}</h2>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {t('morocco_guide_subtitle')}
                    </p>
                  </div>

                  {/* Cards list */}
                  {guideCards.length === 0 ? (
                    <div className={`rounded-2xl border-2 border-dashed p-10 text-center ${isDarkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                      <div className="text-4xl mb-2">📖</div>
                      <div className="text-sm font-bold">{t('morocco_guide_coming_soon_title')}</div>
                      <div className="text-xs mt-1">{t('morocco_guide_coming_soon_desc')}</div>
                    </div>
                  ) : (
                    guideCards.map(card => {
                      const isOpen = expandedGuideCardId === card.id;
                      return (
                        <article
                          key={card.id}
                          className={`rounded-none sm:rounded-2xl border-y sm:border overflow-hidden transition-shadow ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200 shadow-sm hover:shadow-md'}`}
                        >
                          <button
                            onClick={() => setExpandedGuideCardId(isOpen ? null : card.id)}
                            className={`w-full px-2.5 sm:px-4 py-3 flex items-center gap-2 sm:gap-3 text-left ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
                            aria-expanded={isOpen}
                          >
                            <span className="text-2xl sm:text-3xl shrink-0">{card.icon || '📖'}</span>
                            <div className="flex-1 min-w-0">
                              <div className={`font-extrabold text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{card.title}</div>
                              {!isOpen && card.content && (
                                <div className={`text-xs truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {card.content.split('\n')[0]}
                                </div>
                              )}
                            </div>
                            <span className={`text-sm shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {isOpen ? '▲' : '▼'}
                            </span>
                          </button>
                          {isOpen && card.content && (
                            <div className={`px-4 pb-4 pt-1 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                              <pre className={`text-sm whitespace-pre-wrap font-sans leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {card.content}
                              </pre>
                            </div>
                          )}
                        </article>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
          {/* ACTIVITY SIDEBAR — dual purpose:
                Desktop (lg+): static right-side column with the 4 widgets (Prayer / Weekly / Converter / Daily Goal).
                Mobile (<lg): hidden by default; opens as a slide-in drawer from the RIGHT when
                              the user taps the floating widget button (mobileWidgetsOpen state).
              The same JSX powers both — `lg:` overrides bring back the static-sidebar layout.
              `key` is bumped whenever the mobile drawer opens so React re-mounts the scrollable
              aside, guaranteeing scroll position resets to TOP every time. Without this, opening
              the drawer when the user had previously scrolled down would land them mid-widgets,
              hiding the Prayer card at the top.
              IMPORTANT: when the mobile drawer is OPEN, we createPortal the aside to document.body
              so it escapes any `display: none` ancestor (specifically: the content column is hidden
              when activeTab === 'markers', which would otherwise hide the drawer too). On desktop
              the drawer is never open, so the portal branch is unreached and the aside renders
              inline as the static sidebar — desktop layout is unchanged. */}
          {(() => {
            const asideJSX = (
            <aside
            key={`widgets-aside-${mobileWidgetsOpen ? 'open' : 'closed'}`}
            className={`
              ${mobileWidgetsOpen
                ? 'fixed inset-y-0 right-0 z-[1500] block w-[85vw] max-w-sm shadow-2xl space-y-4 overscroll-contain'
                : 'hidden'}
              lg:static lg:inset-auto lg:right-auto lg:flex lg:flex-col lg:gap-4 lg:space-y-0 lg:w-80 lg:max-w-none lg:shadow-none lg:z-auto
              p-5 pb-24 lg:pb-5 border-l overflow-y-auto
              ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-100'}
            `}>
            {/* Mobile-only drawer header with close button (hidden on desktop).
                Plain layout — no sticky, no negative margins — so it can't interfere with
                the flex layout below or accidentally overlap the prayer card. */}
            <div className={`lg:hidden flex items-center justify-between pb-3 mb-1 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div>
                <div className={`text-[10px] uppercase tracking-wider font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Travel Tools</div>
                <div className={`text-base font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Quick Widgets</div>
              </div>
              <button
                onClick={() => setMobileWidgetsOpen(false)}
                className={`w-9 h-9 rounded-full flex items-center justify-center text-lg font-bold ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                aria-label={t('morocco_close_widgets')}
              >
                ✕
              </button>
            </div>
            {/* 🕌 PRAYER CARD — full-sized, big, top of sidebar. Header + countdown banner + 5-prayer grid. */}
            {widgetsConfig.prayer?.enabled !== false && (() => {
              const prayerCfg = widgetsConfig.prayer || _WIDGETS_FALLBACK.prayer;
              const prayerCities = prayerCfg.cities && prayerCfg.cities.length > 0 ? prayerCfg.cities : _WIDGETS_FALLBACK.prayer.cities;
              const order = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
              const prayerEmoji = { Fajr: '🌅', Dhuhr: '☀️', Asr: '🌤️', Maghrib: '🌇', Isha: '🌙' };
              // Display-name override from admin. Canonical keys (Fajr/Dhuhr/...) stay for API data lookup;
              // these labels are what the user sees on the widget. Blank/missing falls back to the key itself.
              const prayerNames = prayerCfg.names || {};
              const displayName = (k) => (prayerNames[k] && prayerNames[k].trim()) || k;
              // Compare prayer times against MOROCCO's current time (from API timezone), not browser local.
              const timings = prayerTimes?.timings || null;
              const tz = prayerTimes?.timezone || 'Africa/Casablanca';
              const nowMins = _nowMinutesInTz(tz);
              let nextIdx = -1, untilStr = null;
              if (timings) {
                for (let i = 0; i < order.length; i++) {
                  const tStr = _stripTzSuffix(timings[order[i]]);
                  if (!/^\d{1,2}:\d{2}$/.test(tStr || '')) continue;
                  const [hh, mm] = tStr.split(':').map(n => parseInt(n, 10));
                  const pMins = hh * 60 + mm;
                  if (pMins > nowMins) {
                    nextIdx = i;
                    const diff = pMins - nowMins;
                    untilStr = diff < 60 ? `${diff}m` : `${Math.floor(diff / 60)}h ${diff % 60}m`;
                    break;
                  }
                }
              }
              const nextName = nextIdx >= 0 ? order[nextIdx] : null;
              const nextTime = nextName ? _stripTzSuffix(timings?.[nextName]) : null;
              return (
                <div className={`rounded-2xl shadow-md border overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
                  {/* Hero banner — gradient header with city selector + next prayer countdown. */}
                  <div className="px-4 pt-4 pb-3 bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-700 text-white">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xl">🕌</span>
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-wider font-bold opacity-80 leading-none">{t('morocco_prayer_5_today')}</div>
                          <div className="text-sm font-extrabold truncate">{t('morocco_prayer_times')}</div>
                        </div>
                      </div>
                      <select
                        value={prayerCity}
                        onChange={(e) => { setPrayerCity(e.target.value); try { localStorage.setItem('mmPrayerCity', e.target.value); } catch {} }}
                        className="text-[11px] font-bold rounded-md px-1.5 py-1 border-none bg-white/20 text-white focus:outline-none focus:ring-2 focus:ring-white/50 shrink-0"
                        title={t('morocco_change_prayer_city')}
                      >
                        {prayerCities.map(c => <option key={c} value={c} className="text-gray-900">{c}</option>)}
                      </select>
                    </div>
                    {/* Countdown to next prayer */}
                    {nextName && nextTime ? (
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl">{prayerEmoji[nextName]}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-wider font-bold opacity-80 leading-none">{t('morocco_prayer_next')}</div>
                          <div className="text-base font-extrabold truncate">{displayName(nextName)} · {nextTime}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[10px] uppercase tracking-wider font-bold opacity-80 leading-none">in</div>
                          <div className="text-sm font-extrabold">{untilStr}</div>
                        </div>
                      </div>
                    ) : prayerLoading ? (
                      <div className="text-xs font-bold opacity-90">Loading prayer times…</div>
                    ) : timings ? (
                      <div className="text-xs font-bold opacity-90">All today's prayers passed · next is {displayName('Fajr')} tomorrow</div>
                    ) : (
                      <div className="text-xs font-bold opacity-90">Prayer times unavailable</div>
                    )}
                  </div>
                  {/* 5-prayer grid below the banner. Always renders — uses placeholders when timings
                      haven't loaded so the card always has real height. Mobile uses larger text/padding;
                      lg: overrides restore the tighter desktop look. */}
                  <div className="p-3 lg:p-3">
                    <div className="grid grid-cols-5 gap-2 lg:gap-1.5">
                      {order.map((name, i) => {
                        const tStr = timings ? _stripTzSuffix(timings[name]) : '';
                        const isPast = timings && (i < nextIdx || nextIdx === -1);
                        const isNext = timings && i === nextIdx;
                        return (
                          <div
                            key={name}
                            className={`text-center py-3 lg:py-2 px-1 rounded-lg transition-all ${
                              isNext
                                ? (isDarkMode ? 'bg-emerald-900/60 ring-2 ring-emerald-500' : 'bg-emerald-50 ring-2 ring-emerald-500')
                                : isPast
                                  ? (isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50')
                                  : (isDarkMode ? 'bg-gray-700/50' : 'bg-emerald-50/40')
                            }`}
                            title={isNext ? 'Next prayer' : isPast ? 'Past' : 'Upcoming'}
                          >
                            <div className={`text-xl lg:text-sm leading-none ${isPast && !isNext ? 'opacity-40' : ''}`}>{prayerEmoji[name]}</div>
                            <div className={`text-[10px] lg:text-[9px] font-bold uppercase tracking-wider mt-1.5 lg:mt-1 leading-none ${
                              isNext ? (isDarkMode ? 'text-emerald-200' : 'text-emerald-700')
                                : isPast ? (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                                : (isDarkMode ? 'text-gray-300' : 'text-gray-600')
                            }`}>{displayName(name).slice(0, 4)}</div>
                            <div className={`text-xs lg:text-[10px] font-extrabold mt-1 lg:mt-0.5 ${
                              isNext ? (isDarkMode ? 'text-white' : 'text-gray-900')
                                : isPast ? (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                                : (isDarkMode ? 'text-white' : 'text-gray-700')
                            }`}>{tStr || '—:—'}</div>
                          </div>
                        );
                      })}
                    </div>
                    {!timings && (
                      <div className={`mt-3 text-center text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {prayerLoading ? 'Loading prayer times…' : 'Could not load prayer times. Tap a city to retry.'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            {widgetsConfig.weekly_progress?.enabled !== false && (
            <div className={`rounded-2xl shadow-sm border p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
              <div className={`text-[10px] uppercase tracking-wider font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('morocco_activity_label')}</div>
              <div className={`text-base font-extrabold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('morocco_weekly_progress')}</div>
              {(() => {
                const labels = ['M','T','W','T','F','S','S'];
                const today = new Date();
                const counts = [0,0,0,0,0,0,0];
                phrases.forEach(p => {
                  if (!p.created_at) return;
                  const d = new Date(p.created_at);
                  const diffDays = Math.floor((today - d) / 86400000);
                  if (diffDays >= 0 && diffDays < 7) {
                    const dayIdx = (d.getDay() + 6) % 7;
                    counts[dayIdx] += 1;
                  }
                });
                const maxCount = Math.max(...counts, 1);
                const totalWeek = counts.reduce((a, b) => a + b, 0);
                const todayIdx = (today.getDay() + 6) % 7;
                // Connected-dots "stock chart" layout. SVG renders the line + area; positioned
                // divs render the dots on top (so the dots stay perfectly circular even when the
                // SVG is stretched to the container width via preserveAspectRatio="none").
                const points = counts.map((c, i) => {
                  const xPct = (i * 100) / 6;
                  const yPct = 100 - (c / maxCount) * 80 - 10; // 10% padding top/bottom
                  return { xPct, yPct };
                });
                const linePoints = points.map(p => `${p.xPct.toFixed(2)},${p.yPct.toFixed(2)}`).join(' ');
                const areaPoints = `${linePoints} 100,100 0,100`;
                const lineColor = isDarkMode ? '#60a5fa' : '#3b82f6'; // blue-400 / blue-500
                const todayDot = isDarkMode ? '#ffffff' : '#0f172a';
                return (
                  <>
                    {/* Chart area: fixed 110px tall, full-width responsive */}
                    <div className="relative h-[110px] w-full">
                      <svg
                        className="absolute inset-0 w-full h-full"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                        aria-hidden="true"
                      >
                        <defs>
                          <linearGradient id="weeklyArea" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%" stopColor={lineColor} stopOpacity="0.35" />
                            <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        {/* Faint area fill under the line — gives the "stock chart" feel */}
                        <polygon points={areaPoints} fill="url(#weeklyArea)" />
                        {/* Connecting line */}
                        <polyline
                          points={linePoints}
                          fill="none"
                          stroke={lineColor}
                          strokeWidth="2"
                          vectorEffect="non-scaling-stroke"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {/* Dots overlay — always perfectly circular regardless of container width */}
                      {points.map((p, i) => {
                        const isToday = i === todayIdx;
                        return (
                          <div
                            key={i}
                            className={`absolute rounded-full transition-transform ${isToday ? 'w-3 h-3' : 'w-2 h-2'}`}
                            style={{
                              left: `${p.xPct}%`,
                              top: `${p.yPct}%`,
                              transform: 'translate(-50%, -50%)',
                              background: isToday ? todayDot : lineColor,
                              boxShadow: `0 0 0 ${isToday ? 2 : 1.5}px ${isDarkMode ? '#1f2937' : '#ffffff'}`,
                            }}
                            title={`${labels[i]} · ${counts[i]} phrase${counts[i] === 1 ? '' : 's'}`}
                          />
                        );
                      })}
                    </div>
                    {/* Day labels */}
                    <div className="flex items-center justify-between gap-1.5 mt-2">
                      {labels.map((l, i) => (
                        <div key={i} className={`flex-1 text-center text-[10px] font-bold ${i === todayIdx ? (isDarkMode ? 'text-white' : 'text-slate-900') : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                          {l}
                        </div>
                      ))}
                    </div>
                    {/* Per-day counts */}
                    <div className="flex items-center justify-between gap-1.5 mt-1">
                      {counts.map((c, i) => (
                        <div key={i} className={`flex-1 text-center text-[10px] font-extrabold ${i === todayIdx ? (isDarkMode ? 'text-white' : 'text-slate-900') : (isDarkMode ? 'text-gray-500' : 'text-gray-400')}`}>
                          {c}
                        </div>
                      ))}
                    </div>
                    <div className={`mt-4 pt-3 border-t flex items-center justify-between text-xs ${isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-100 text-gray-500'}`}>
                      <span className="font-bold uppercase tracking-wider text-[10px]">{t('morocco_7_day_total')}</span>
                      <span className={`font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{totalWeek} {totalWeek === 1 ? t('morocco_phrase_count_one') : t('morocco_phrase_count_many')}</span>
                    </div>
                  </>
                );
              })()}
            </div>
            )}

            {/* 💱 MAD CONVERTER — quick foreign-to-MAD math. Live rates daily (free API) with fallback. */}
            {widgetsConfig.converter?.enabled !== false && (() => {
              const convCfg = widgetsConfig.converter || _WIDGETS_FALLBACK.converter;
              const currencies = (convCfg.currencies && convCfg.currencies.length > 0) ? convCfg.currencies : ['EUR', 'USD', 'GBP'];
              const fallbackRates = convCfg.fallback_rates_per_mad || _FALLBACK_RATES_PER_1_MAD;
              // Ensure convFrom is one of the configured currencies
              const effectiveFrom = currencies.includes(convFrom) ? convFrom : currencies[0];
              return (
                <div className={`rounded-2xl shadow-sm border p-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className={`text-[10px] uppercase tracking-wider font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('morocco_mad_converter_desc')}</div>
                      <div className={`text-base font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('morocco_mad_converter')}</div>
                    </div>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${convRatesLive ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {convRatesLive ? 'LIVE' : 'EST.'}
                    </span>
                  </div>
                  {(() => {
                    // Clamp input: HTML `min` is advisory (users can paste negative or huge values).
                    // Hard-cap to [0, 1B] so result never shows nonsense like "-536 MAD" or "107 trillion".
                    const rawInput = parseFloat(convAmount);
                    const amount = Number.isFinite(rawInput) ? Math.max(0, Math.min(1e9, rawInput)) : 0;
                    // rateForeignPerMAD: e.g. USD = 0.109 means 1 MAD = 0.109 USD (so 1 USD = 9.17 MAD)
                    const rateForeignPerMAD = convRates[effectiveFrom] || fallbackRates[effectiveFrom] || 0.10;
                    // Both directions computed from the same rate:
                    //   foreign → MAD: multiply by (1 / rate)
                    //   MAD → foreign: multiply by rate
                    const factor = convForeignToMad ? (1 / rateForeignPerMAD) : rateForeignPerMAD;
                    const raw = amount * factor;
                    // Always show 2 decimals so 1 USD shows 9.17 MAD (not rounded to 9).
                    // Big numbers like 1,090.45 MAD stay readable; small numbers keep their precision.
                    const result = raw.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
                    const sourceCurrencyLabel = convForeignToMad ? effectiveFrom : 'MAD';
                    const targetCurrencyLabel = convForeignToMad ? 'MAD' : effectiveFrom;
                    return (
                      <>
                        {/* Input row */}
                        <div className="flex gap-2 mb-2">
                          <input
                            type="number"
                            min="0"
                            max="1000000000"
                            inputMode="decimal"
                            value={convAmount}
                            onChange={(e) => setConvAmount(e.target.value)}
                            className={`flex-1 min-w-0 px-2.5 py-1.5 rounded-lg text-sm font-bold border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                          />
                          {convForeignToMad ? (
                            <select
                              value={effectiveFrom}
                              onChange={(e) => setConvFrom(e.target.value)}
                              className={`px-2 py-1.5 rounded-lg text-sm font-bold border min-w-[68px] ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                              title={t('morocco_source_currency')}
                            >
                              {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          ) : (
                            <div className={`px-3 py-1.5 rounded-lg text-sm font-bold border min-w-[68px] flex items-center justify-center ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>MAD</div>
                          )}
                        </div>
                        {/* Swap button */}
                        <div className="flex justify-center mb-2">
                          <button
                            type="button"
                            onClick={() => setConvForeignToMad(v => !v)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                            title={`Switch to ${convForeignToMad ? 'MAD → ' + effectiveFrom : effectiveFrom + ' → MAD'}`}
                          >
                            <span className="text-sm">⇅</span>
                            <span>Swap</span>
                            <span className="opacity-60">{sourceCurrencyLabel}→{targetCurrencyLabel}</span>
                          </button>
                        </div>
                        {/* Output row */}
                        <div className="flex gap-2 items-stretch">
                          <div className={`flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border flex items-baseline gap-1 ${isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
                            <span className="text-2xl font-extrabold truncate">{result}</span>
                          </div>
                          {convForeignToMad ? (
                            <div className={`px-3 py-1.5 rounded-lg text-sm font-bold border min-w-[68px] flex items-center justify-center ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>MAD</div>
                          ) : (
                            <select
                              value={effectiveFrom}
                              onChange={(e) => setConvFrom(e.target.value)}
                              className={`px-2 py-1.5 rounded-lg text-sm font-bold border min-w-[68px] ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                              title={t('morocco_target_currency')}
                            >
                              {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          )}
                        </div>
                        <div className={`text-[10px] mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {t('morocco_rates_disclaimer')}
                        </div>
                      </>
                    );
                  })()}
                </div>
              );
            })()}

            {/* 🎯 DAILY GOAL — phrases-per-day target with ring progress. Goal stored in localStorage. */}
            {widgetsConfig.daily_goal?.enabled !== false && (
            <div className={`rounded-2xl shadow-sm border p-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
              <div className={`text-[10px] uppercase tracking-wider font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('morocco_daily_goal_desc')}</div>
              <div className={`text-base font-extrabold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('morocco_daily_goal')}</div>
              {(() => {
                const todayStr = new Date().toISOString().split('T')[0];
                const doneToday = phrases.filter(p => (p.created_at || '').startsWith(todayStr)).length;
                const pct = Math.min(100, Math.round((doneToday / dailyGoal) * 100));
                const radius = 32;
                const circumference = 2 * Math.PI * radius;
                const offset = circumference - (pct / 100) * circumference;
                const done = doneToday >= dailyGoal;
                return (
                  <div className="flex items-center gap-3">
                    {/* SVG ring */}
                    <div className="relative w-20 h-20 shrink-0">
                      <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                        <circle cx="40" cy="40" r={radius} strokeWidth="6" fill="none" className={isDarkMode ? 'stroke-gray-700' : 'stroke-gray-100'} />
                        <circle
                          cx="40" cy="40" r={radius} strokeWidth="6" fill="none" strokeLinecap="round"
                          className={done ? 'stroke-emerald-500' : 'stroke-blue-500'}
                          strokeDasharray={circumference}
                          strokeDashoffset={offset}
                          style={{ transition: 'stroke-dashoffset 400ms ease' }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className={`text-xl font-extrabold leading-none ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{doneToday}<span className={`text-xs font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>/{dailyGoal}</span></span>
                        <span className={`text-[9px] font-bold mt-0.5 ${done ? 'text-emerald-600' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`}>{done ? 'DONE' : 'phrases'}</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-bold mb-1 ${done ? 'text-emerald-600' : (isDarkMode ? 'text-gray-300' : 'text-gray-700')}`}>
                        {done ? t('morocco_goal_reached') : `${dailyGoal - doneToday} ${t('morocco_to_go')}`}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => _updateDailyGoal(-1)} className={`w-7 h-7 rounded-md text-sm font-bold transition-colors ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} title={t('morocco_lower_goal')}>−</button>
                        <div className={`flex-1 text-center text-[11px] font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('morocco_daily_goal')}: {dailyGoal}</div>
                        <button onClick={() => _updateDailyGoal(1)} className={`w-7 h-7 rounded-md text-sm font-bold transition-colors ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} title={t('morocco_raise_goal')}>+</button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            )}

            {/* ⚠️ DISCLAIMER — MOBILE only inside the widgets drawer (lg:hidden). Sits at the bottom
                under Daily Goal. On desktop this lives in a separate yellow bar below the page. */}
            <div className={`lg:hidden rounded-2xl shadow-sm border p-4 ${isDarkMode ? 'bg-yellow-900/20 border-yellow-700/50 text-yellow-200' : 'bg-yellow-50 border-yellow-200 text-yellow-800'}`}>
              <div className="flex items-start gap-2">
                <span className="text-lg flex-shrink-0 leading-none">⚠️</span>
                <p className="text-[11px] leading-relaxed">
                  {settings.disclaimer_text || "All notes and entries are private and visible only to you."}
                </p>
              </div>
            </div>
          </aside>
            );
            return mobileWidgetsOpen ? createPortal(asideJSX, document.body) : asideJSX;
          })()}
          </div>
        </div>
      </div>

      {/* Desktop disclaimer bar removed — replaced by a small ⚠️ icon on the map bottom-left
          that opens the modal below. Mobile still shows the inline notice inside the widgets drawer. */}

      {/* Small disclaimer modal — opens from the map ⚠️ icon (desktop). */}
      {showDisclaimerModal && (
        <div
          className="fixed inset-0 z-[3000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={() => setShowDisclaimerModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800 text-yellow-200 border border-yellow-700/40' : 'bg-white text-yellow-800 border border-yellow-200'}`}
          >
            <div className={`px-5 py-3 flex items-center justify-between ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
              <div className="flex items-center gap-2">
                <span className="text-xl">⚠️</span>
                <h3 className="text-sm font-extrabold uppercase tracking-wider">{t('morocco_privacy_disclaimer')}</h3>
              </div>
              <button
                onClick={() => setShowDisclaimerModal(false)}
                className={`w-7 h-7 rounded-md text-sm font-bold ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-yellow-100'}`}
                aria-label={t('close')}
              >
                ✕
              </button>
            </div>
            <div className="px-5 py-4 text-sm leading-relaxed">
              {settings.disclaimer_text || t('morocco_privacy_modal_fallback')}
            </div>
          </div>
        </div>
      )}
      
      {/* CSS for map markers */}
      <style>{`
        .emoji-marker {
          background: none;
          border: none;
        }
        .city-marker-container {
          background: none;
          border: none;
        }
        .city-marker-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
        }
        .city-dot-new {
          width: 8px;
          height: 8px;
          background: #c41e3a;
          border-radius: 50%;
          border: 2px solid white;
          box-shadow: 0 1px 3px rgba(0,0,0,0.4);
        }
        .city-name-label {
          font-size: 9px;
          font-weight: 700;
          color: #1f2937;
          text-shadow: 1px 1px 0 white, -1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 0 1px 0 white, 0 -1px 0 white;
          white-space: nowrap;
          text-align: center;
          letter-spacing: 0.3px;
        }
        .leaflet-container.cursor-crosshair {
          cursor: crosshair !important;
        }

        /* ── Nicer existing-marker popup balloon ─────────────────────────── */
        .leaflet-popup.tt-marker-popup .leaflet-popup-content-wrapper {
          padding: 0;
          border-radius: 14px;
          box-shadow: 0 12px 30px rgba(0,0,0,0.18), 0 4px 8px rgba(0,0,0,0.08);
          overflow: hidden;
          background: #ffffff;
        }
        .leaflet-popup.tt-marker-popup .leaflet-popup-content {
          margin: 0;
          padding: 0;
          min-width: 200px;
          max-width: 280px;
        }
        .leaflet-popup.tt-marker-popup .leaflet-popup-tip {
          background: #ffffff;
        }
        .tt-marker-card {
          font-family: inherit;
        }
        .tt-marker-card-head {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px 10px 14px;
          background: linear-gradient(135deg, #f0f9ff 0%, #ecfeff 100%);
          border-bottom: 1px solid rgba(0,0,0,0.05);
        }
        .tt-marker-card-emoji {
          font-size: 26px;
          line-height: 1;
          width: 38px;
          height: 38px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #ffffff;
          border-radius: 50%;
          box-shadow: 0 2px 6px rgba(0,0,0,0.08);
        }
        .tt-marker-card-title {
          font-weight: 800;
          font-size: 15px;
          color: #0f172a;
          line-height: 1.25;
          word-break: break-word;
        }
        .tt-marker-card-body {
          padding: 10px 14px;
          font-size: 13px;
          color: #334155;
          line-height: 1.45;
          margin: 0;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .tt-marker-card-city {
          padding: 8px 14px 12px 14px;
          font-size: 11px;
          font-weight: 600;
          color: #475569;
          display: flex;
          align-items: center;
          gap: 5px;
          border-top: 1px solid rgba(0,0,0,0.04);
          background: #f8fafc;
        }
        /* MOBILE: zoom on the LEFT side, below the floating "Map · N" header pill (which is
           at top:12px on the left). Header pill is ~32px tall + 8px gap → zoom at top:52px. */
        @media (max-width: 1023px) {
          .leaflet-top.leaflet-left {
            top: 52px;
            left: 12px;
          }
          .leaflet-top.leaflet-left .leaflet-control {
            margin-top: 0;
            margin-left: 0;
          }
        }
        /* On desktop, push the top-left zoom control down so it sits BELOW the
           floating "Travel Tools · Map · N markers" header pill instead of overlapping it. */
        @media (min-width: 1024px) {
          .leaflet-top.leaflet-left {
            top: 76px;
            left: 16px;
          }
          .leaflet-top.leaflet-left .leaflet-control {
            margin-top: 0;
            margin-left: 0;
          }
        }
        /* Hide default Leaflet attribution on mobile */
        @media (max-width: 640px) {
          .leaflet-control-attribution {
            font-size: 8px;
          }
        }
        /* Make map cleaner */
        .leaflet-tile-pane {
          filter: saturate(0.8);
        }
      `}</style>
      
      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000]">
          <div className={`p-6 rounded-xl max-w-sm w-full mx-4 shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-center">
              <div className="text-5xl mb-4">🗑️</div>
              <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {deleteModal.type === 'list' ? t('morocco_delete_list_title') : deleteModal.type === 'item' ? t('morocco_delete_item_title') : t('morocco_delete_phrase_title')}
              </h3>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('morocco_confirm_delete_question')} &ldquo;<span className="font-medium">{deleteModal.name}</span>&rdquo;?
                <br />
                <span className="text-xs text-red-500">
                  {deleteModal.type === 'list' ? t('morocco_delete_list_warning') : t('morocco_delete_undone')}
                </span>
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteModal({ show: false, type: '', id: '', name: '' })}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={() => {
                    if (deleteModal.type === 'phrase') deletePhrase(deleteModal.id);
                    else if (deleteModal.type === 'list') deleteList(deleteModal.id);
                    else if (deleteModal.type === 'item') deleteItem(deleteModal.id);
                    else if (deleteModal.type === 'wish') deleteWishItem(deleteModal.id);
                  }}
                  className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors"
                >
                  {t('morocco_yes_delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Budget Confirmation Modal */}
      {showDeleteBudgetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
          <div className={`p-6 rounded-2xl max-w-sm w-full mx-4 shadow-2xl transform transition-all ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-3xl">🗑️</span>
              </div>
              <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {t('morocco_delete_budget_title')}
              </h3>
              <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('morocco_delete_budget_warning')}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteBudgetModal(false)}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={() => {
                    deleteBudgetSettings();
                    setShowDeleteBudgetModal(false);
                  }}
                  className="flex-1 py-3 px-4 rounded-xl font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
                >
                  {t('morocco_delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Documentation Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
          <div className={`rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Header */}
            <div className={`p-4 border-b flex items-center justify-between shrink-0 ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gradient-to-r from-green-500 to-blue-500'}`}>
              <div className="flex items-center gap-3">
                <span className="text-3xl">🇲🇦</span>
                <div>
                  <h2 className="text-xl font-bold text-white">{guideData?.title || 'My Morocco Guide'}</h2>
                  <p className="text-sm text-white/80">{guideData?.subtitle || 'Your personal travel companion'}</p>
                </div>
              </div>
              <button
                onClick={() => setShowHelpModal(false)}
                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
              >
                ✕
              </button>
            </div>
            
            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              
              {/* Introduction */}
              {guideData?.intro && (
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                  <p className={`text-sm whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {guideData.intro}
                  </p>
                </div>
              )}

              {/* Dynamic Guide Sections */}
              {guideData?.items?.map((section, index) => (
                <div key={section.id || index}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">{section.icon}</span>
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{section.title}</h3>
                  </div>
                  {section.content && (
                    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className={`text-sm whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {section.content}
                      </p>
                    </div>
                  )}
                </div>
              ))}

            </div>
            
            {/* Footer */}
            <div className={`p-4 border-t shrink-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowHelpModal(false)}
                className="w-full py-3 rounded-xl font-medium bg-gradient-to-r from-green-500 to-blue-500 text-white hover:opacity-90 transition-opacity"
              >
                Got it! Let&apos;s explore 🚀
              </button>
            </div>
          </div>
        </div>
      )}

      {showProGlass && (
        <>
          <div className="absolute inset-0 z-20 cursor-not-allowed"
               style={{ backdropFilter: 'blur(1px)', WebkitBackdropFilter: 'blur(1px)',
                        backgroundColor: isDarkMode ? 'rgba(17,24,39,0.04)' : 'rgba(255,255,255,0.04)' }} />
          <div className="fixed z-30 pointer-events-none px-4" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(90vw, 28rem)' }}>
            <div className={`pointer-events-auto text-center p-5 sm:p-8 rounded-3xl shadow-2xl ring-4 ring-white/30 ${isDarkMode ? 'bg-gray-900/95' : 'bg-white/95'}`}>
              <span className="text-4xl sm:text-6xl mb-2 sm:mb-4 block">🗺️</span>
              <h2 className={`text-xl sm:text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Travel Tools</h2>
              <p className={`text-sm sm:text-base mb-4 sm:mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Pro feature. Unlock the interactive map, travel phrases, budget planner, and more.
              </p>
              <button
                onClick={() => { if (setInitialProfileTab) setInitialProfileTab('membership'); if (setCurrentView) setCurrentView('profile'); }}
                className="px-5 sm:px-6 py-2.5 sm:py-3 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold shadow-2xl hover:scale-105 transition-all flex items-center gap-2 mx-auto cursor-pointer">
                <span className="text-lg sm:text-xl">🔓</span><span>Upgrade to Pro</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Custom confirmation dialog (replaces window.confirm browser popup).
          z-[4000] sits above everything else: fullscreen map (z-2000), emoji picker (z-3000),
          widgets drawer (z-1500), markers panel (z-1050). Otherwise the dialog renders
          BEHIND the markers list and the user's "Delete" tap hits the panel instead of the button. */}
      {confirmDialog && (
        <div className="fixed inset-0 z-[4000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setConfirmDialog(null)}>
          <div className={`max-w-sm w-full rounded-2xl shadow-2xl p-6 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`} onClick={(e) => e.stopPropagation()}>
            <p className={`text-base font-medium mb-5 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{confirmDialog.message}</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDialog(null)} className={`px-4 py-2 rounded-lg font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{t('cancel')}</button>
              <button onClick={async () => { const fn = confirmDialog.onYes; setConfirmDialog(null); if (fn) await fn(); }} className="px-4 py-2 rounded-lg font-medium bg-red-500 text-white hover:bg-red-600 transition-colors">{t('morocco_delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Premium gradient palette — modern colorful card backgrounds
const CARD_GRADIENTS = [
  'from-blue-500 via-indigo-500 to-purple-600',
  'from-pink-500 via-rose-500 to-orange-500',
  'from-emerald-500 via-teal-500 to-cyan-600',
  'from-amber-500 via-orange-500 to-red-500',
  'from-cyan-500 via-blue-500 to-indigo-600',
  'from-fuchsia-500 via-purple-500 to-indigo-600',
  'from-lime-500 via-emerald-500 to-teal-600',
  'from-violet-500 via-purple-600 to-fuchsia-600',
];

const pickGradient = (key) => {
  const s = String(key || 'x');
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return CARD_GRADIENTS[hash % CARD_GRADIENTS.length];
};

// Phrase Card Component - Shows all 3 languages
// Category accent color for the small dot in card top-right
const CATEGORY_ACCENT = {
  greetings: 'bg-emerald-400',
  amazigh: 'bg-violet-400',
  north_dialect: 'bg-cyan-400',
  family_phrases: 'bg-rose-400',
  family: 'bg-rose-400',
  other: 'bg-slate-400',
};
const getCategoryAccent = (catId) => CATEGORY_ACCENT[catId] || 'bg-slate-400';

// Flag chip color theme per language slot
const FLAG_CHIP = {
  text_english: { bg: 'bg-sky-50', text: 'text-sky-700', ring: 'ring-sky-100', label: 'EN' },
  text_darija: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-100', label: 'MA' },
  text_third_lang: { bg: 'bg-violet-50', text: 'text-violet-700', ring: 'ring-violet-100', label: 'ⵣ' },
};
const getFlagChip = (fieldId, fallbackIcon) => {
  if (FLAG_CHIP[fieldId]) return FLAG_CHIP[fieldId];
  return { bg: 'bg-slate-50', text: 'text-slate-700', ring: 'ring-slate-100', label: fallbackIcon || '•' };
};

const PhraseCard = ({ phrase, isDarkMode, onEdit, onDelete, onMoveUp, onMoveDown, getCityName, getCategoryName, compact = false, phraseFields = [] }) => {
  const { t } = useTranslation();
  // Get values with backward compatibility for default fields
  const english = phrase.text_english || phrase.text || '';
  const darija = phrase.text_darija || phrase.translation || '';
  const thirdLang = phrase.text_third_lang || phrase.family_member || '';

  // Third-language chip is ALWAYS the Amazigh letter ⵣ — the user can type whatever
  // they want in the field (Amazigh, French, blank…); the chip stays consistent and
  // never shows the speaker 🗣️ emoji, which read as "someone speaking" not as a language.
  const getThirdLangIcon = () => 'ⵣ';

  const getFieldValue = (fieldId) => {
    if (fieldId === 'text_english') return english;
    if (fieldId === 'text_darija') return darija;
    if (fieldId === 'text_third_lang') return thirdLang;
    return phrase[fieldId] || '';
  };

  const fieldsToShow = phraseFields.length > 0 ? phraseFields : [
    { id: 'text_english', name: 'English', icon: '🇬🇧' },
    { id: 'text_darija', name: 'Darija', icon: '🇲🇦' },
    { id: 'text_third_lang', name: 'Other', icon: '🗣️' }
  ];
  const accentDot = getCategoryAccent(phrase.category);

  return (
    <article className={`relative rounded-2xl transition-all hover:-translate-y-0.5 ${compact ? 'p-4' : 'p-5'} ${isDarkMode ? 'bg-gray-800 border border-gray-700 shadow-md' : 'bg-white border border-gray-100 shadow-card'}`}
      style={{ boxShadow: isDarkMode ? '0 6px 24px -8px rgba(0,0,0,0.5)' : '0 6px 24px -8px rgba(15,23,42,0.08)' }}>
      {/* Category accent dot — top right */}
      <div className={`absolute top-4 right-4 w-2.5 h-2.5 rounded-full ${accentDot}`}></div>

      {/* All language rows with flag chips — including English. Consistent visual weight
          across rows; no more oversized title for the first field. */}
      <div className="space-y-1.5 pr-6">
        {fieldsToShow.map(field => {
          const value = getFieldValue(field.id);
          const isThird = field.id === 'text_third_lang';
          const labelOverride = isThird ? getThirdLangIcon() : null;
          const chip = getFlagChip(field.id, labelOverride || field.icon);
          if (!value) return null;
          return (
            <div key={field.id} className="flex items-center gap-2.5">
              <span className={`shrink-0 inline-flex items-center justify-center w-9 h-6 rounded-lg ${chip.bg} ${chip.text} text-[11px] font-bold ring-1 ${chip.ring}`}>
                {isThird ? (labelOverride || chip.label) : (chip.label)}
              </span>
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {value}
              </span>
            </div>
          );
        })}
      </div>

      {/* Footer: city badge + action strip */}
      <div className={`mt-4 pt-3 flex items-center justify-between border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
        {phrase.city_id ? (
          <span className={`inline-flex items-center gap-1 text-xs font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            📍 {getCityName(phrase.city_id)}
          </span>
        ) : <span></span>}
        <div className="flex items-center gap-0.5">
          <button onClick={onMoveUp} disabled={!onMoveUp}
            className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs ${!onMoveUp ? 'opacity-30 cursor-not-allowed' : isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-400'}`}
            aria-label={t('morocco_move_up')} title={t('morocco_move_up')}>▲</button>
          <button onClick={onMoveDown} disabled={!onMoveDown}
            className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs ${!onMoveDown ? 'opacity-30 cursor-not-allowed' : isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-400'}`}
            aria-label={t('morocco_move_down')} title={t('morocco_move_down')}>▼</button>
          <div className={`w-px h-4 mx-1 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
          <button onClick={onEdit}
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'hover:bg-blue-900/30 text-blue-400' : 'hover:bg-blue-50 text-blue-600'}`}
            aria-label={t('morocco_edit')} title={t('morocco_edit')}>✏️</button>
          <button onClick={onDelete}
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'hover:bg-red-900/30 text-red-400' : 'hover:bg-red-50 text-red-500'}`}
            aria-label={t('morocco_delete')} title={t('morocco_delete')}>🗑️</button>
        </div>
      </div>
    </article>
  );
};

export default TravelTools;
